import React, { useContext, useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from "react";
import { avisarSaldoCambiado } from "../../../lib/saldoBus";
import ReactDOM from "react-dom";
import { UserContext } from "../../../context/UserContext";
import { withStableImageUrls } from "../../../lib/stableImages";
import ClientTransactions from "./ClientTransactions";
import { avisarCarritosCambiados } from "../../../lib/carritosBus";
import { useLocation, useNavigate } from "react-router-dom";
import ProductGridCart from "./ProductGridCart";
import Cart from "./Cart";
import CaducadoChip from "../../Elements/CaducadoChip";
import { filtroBusqueda, getImageUrl, socioNum } from "../../../lib/helpers";
import { fusionarCarrito, conservarDescuentosManuales } from '../../../lib/helpers/cartMerge';
import { nuevaClaveIdempotencia, claveCobroParaHuella, olvidarClaveCobro, fijarClaveCobro } from '../../../lib/helpers/claveIdempotencia';
import MemberPhoto from "../../Elements/MemberPhoto";
import SectionHeader from '../../Elements/SectionHeader';
import SectionCounter from '../../Elements/SectionCounter';
import { formatBalance, balanceToneClass } from '../../../lib/helpers/balance';
import { effectiveUnitPrice } from '../../../lib/helpers/effectivePrice';
// El descuento del socio vive en lib/helpers: la Sala lo necesita igual, y
// tenerlo aquí dentro es lo que hizo que allí no se aplicara (01-09-2026).
import { resolveMemberDiscount } from "../../../lib/helpers/memberDiscount";
import axios from 'axios';
import { useInVenueUsers } from "../../../context/useInVenueUsers";
import apiFetch, { safeJson } from "../../../context/api";
import { BsCart4 } from "react-icons/bs";
import { HiOutlineSearch, HiOutlineChevronDown, HiOutlineClock, HiOutlineRefresh } from "react-icons/hi";
import ClientDetailModal from "../CustomerComponents/ClientDetailModal";
import AddCashModal from "../../Elements/AddCashModal";
import NotaSocioPopup, { notaConAviso } from "../../Elements/NotaSocioPopup";
import CustomerModal from "../CustomerComponents/CustomerModal";
import PaymentModal from "./PaymentModal";
import LocationChoiceModal from "./LocationChoiceModal";
import { esFuenteDeVenta } from "../../../lib/helpers/locations";
import { createAddGuard } from "./addClickGuard";
import "./Dispenser.css";
import CartBar from '../../Elements/CartBar';
import { calcularTotalesCarrito } from '../../../lib/cartTotals';
import CartHeader from '../../Elements/CartHeader';


/**
 * Líneas del carrito en memoria a partir de la respuesta del servidor
 * (`GET /v1/draft-cart` y, desde BE #625, la respuesta de cada incremento:
 * misma forma, RF-008). Una sola manera de pintar el carrito, venga de donde venga.
 */
function lineasDesdeServidor(data, prods) {
  const byLine = {};
  for (const it of data?.items || []) {
    const rawId = it.product_id;
    const pidKey = (rawId == null ? '' : String(rawId)).toLowerCase();
    const loc = it.location_id ?? undefined;
    const key = `${pidKey}|${loc ?? ''}`;  // línea por (producto + ubicación)
    const p = (prods || []).find((pr) => pr && String(pr.id).toLowerCase() === pidKey);
    const qty = Number(it.quantity) || 0;
    if (qty <= 0) continue;
    if (!byLine[key]) {
      byLine[key] = {
        id: rawId,
        name: it.product_name,
        price: Number(it.unit_price),
        quantity: qty,
        real_weight: it.real_weight != null ? Number(it.real_weight) : undefined,
        // Manda lo que dice la línea del servidor («weight» / «fixed», BE rama
        // fix/draft-cart-unit-type-en-espejo): las líneas del OTRO área no están en el catálogo de esta
        // (se carga por área) y caían al 'weight' por defecto — «Agua
        // €0.50 / g · 1 g» (captura de Yago, 20-09-2026). Sin el campo (backend
        // por detrás) se sigue mirando el catálogo.
        unit_type: it.unit_type || (p && p.unit_type) || 'weight',
        image_path: p?.image_path,
        location_id: loc,  // ubicación reservada (Fase D)
        // Cart v2 persiste el «Dto. %» (cart_items.discount_pct); si el
        // servidor lo manda, la línea reconstruida lo trae puesto. El
        // backend legacy no lo manda nunca: queda undefined y lo repone
        // conservarDescuentosManuales desde el carrito en memoria.
        manual_discount: it.discount_pct != null ? Number(it.discount_pct) : undefined,
      };
    } else {
      byLine[key].quantity = Number((byLine[key].quantity + qty).toFixed(3));
      if (it.real_weight != null) byLine[key].real_weight = Number(((byLine[key].real_weight || 0) + Number(it.real_weight)).toFixed(3));
    }
  }
  return Object.values(byLine).filter((item) => item.quantity > 0);
}

/**
 * Foto del servidor en forma de líneas de guardado: es la `base` del merge a tres
 * bandas (`fusionarCarrito`). Una sola forma de construirla, venga del GET o de la
 * respuesta de un incremento: dos copias a mano ya dejaron caer `discount_pct` una vez.
 */
function baseDesdeServidor(data) {
  return (data?.items || []).map((it) => ({
    product_id: it.product_id, location_id: it.location_id ?? null,
    quantity: Number(it.quantity) || 0,
    unit_price: it.unit_price != null ? Number(it.unit_price) : null,
    real_weight: it.real_weight != null ? Number(it.real_weight) : null,
    discount_pct: it.discount_pct != null ? Number(it.discount_pct) : null,
  }));
}

/** La base tras aplicar MI delta: lo que el servidor hizo por mí aunque aún no lo haya repintado. */
function baseConDelta(base, toque) {
  const pid = String(toque.id).toLowerCase();
  const loc = toque.location_id ?? null;
  const lineas = base.map((l) => ({ ...l }));
  let i = lineas.findIndex((l) => String(l.product_id).toLowerCase() === pid && (l.location_id ?? null) === loc);
  if (i < 0 && loc === null) i = lineas.findIndex((l) => String(l.product_id).toLowerCase() === pid);
  if (i >= 0) {
    lineas[i].quantity = Number((lineas[i].quantity + toque.delta).toFixed(3));
    lineas[i].real_weight = toque.real_weight ?? lineas[i].real_weight;   // sustituye si viene
  } else if (toque.delta > 0) {
    lineas.push({ product_id: toque.id, location_id: loc, quantity: toque.delta,
      unit_price: Number(toque.price) || 0, real_weight: toque.real_weight ?? null, discount_pct: null });
  }
  return lineas.filter((l) => l.quantity > 0);
}

function Dispenser({
  embedProductosInControl = false,
  /** Id del socio elegido en Entradas (panel derecho); null = limpiar */
  entriesPanelUserId = null,
  entriesPanelSyncClient = null,
  /** Club social: lista de "Socios en sala" del padre (fuente de verdad para el selector del carrito pendiente) */
  entriesPanelUsersInClub = null,
  onEntriesEmbedClearClient,
  /** Avisar a Entradas cuando el socio se elige aquí (buscador) */
  onEmbedClientSelected,
  /** Club social: sincronizar saldo en la lista "Socios en sala" del padre (otro hook useInVenueUsers) */
  onEmbeddedVenueCreditSync,
  /** Club social: avisar a Entradas para refrescar "tras carrito" / "A pagar" tras guardar borrador */
  onEmbeddedDraftCartSynced,
} = {}) {
  /** En Entradas embebido: tablet vertical comparte breakpoints con móvil hasta lg */
  const compactUntilLg = embedProductosInControl;
  const [token, , , venueID] = useContext(UserContext);
  const [transactions, setTransactions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  // Aviso de stock al teclear cantidades en el carrito. Estado propio para no
  // pisar errorMessage (que lo usan otros flujos) ni que uno borre al otro.
  const [qtyWarning, setQtyWarning] = useState("");
  // ¿El aviso de stock admite seguir adelante? Solo en dispensario: el armario va a
  // negativo por diseño y se reconcilia con la recarga del encargado. En barra el
  // backend rechaza la venta, así que el carrito no puede prometer lo contrario.
  const [qtyWarningAllowsNegative, setQtyWarningAllowsNegative] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [avisoSocio, setAvisoSocio] = useState(null); // nota marcada "abrir al interactuar"
  // Cajón inferior del historial del socio (solo página Dispensario, no embebido en Entradas).
  // Por defecto PLEGADO; el usuario lo despliega con la barra si quiere ver el historial.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadedProducts, setLoadedProducts] = useState(false);
  const [cartRaw, setCart] = useState([]);
  // Items del OTRO área (barra<->dispensario) del mismo socio, solo lectura: "el carrito
  // tiene todo". Lo editable/validable sigue siendo el área actual (separación legal).
  const [otherAreaItems, setOtherAreaItems] = useState([]);
  // Club social (Productos embebidos): permitir carrito "pendiente" sin socio asignado aún
  const [pendingCartRaw, setPendingCartRaw] = useState([]);
  // Clave de idempotencia del COBRO (Kong House, 13-09: «se queda cargando y se me
  // cobra doble»). Una por «este carrito de este socio»: mientras no cambie ni una
  // línea, cerrar y reabrir el modal de pago o reintentar tras un corte de red manda
  // la MISMA clave y el backend devuelve la venta ya hecha en vez de cobrar otra vez.
  // Se renueva al tocar el carrito, al cambiar de socio y al terminar un cobro.
  const claveCobroRef = useRef(nuevaClaveIdempotencia());
  const pendingCartCarryRef = useRef(null);
  // Dedup por (producto + ubicación): el MISMO producto puede estar en 2 neveras a la
  // vez como líneas separadas (cada una descuenta de su sitio). Solo se fusionan
  // líneas con el mismo producto Y la misma ubicación.
  const cart = useMemo(() => {
    if (cartRaw.length === 0) return cartRaw;
    const byKey = {};
    for (const item of cartRaw) {
      const key = `${(item.id == null ? '' : String(item.id)).toLowerCase()}|${item.location_id ?? ''}`;
      if (!byKey[key]) {
        byKey[key] = { ...item };
      } else {
        byKey[key].quantity = Number((byKey[key].quantity + (Number(item.quantity) || 0)).toFixed(3));
      }
    }
    const deduped = Object.values(byKey);
    return deduped.length === cartRaw.length ? cartRaw : deduped;
  }, [cartRaw]);
  const location = useLocation();
  const navigate = useNavigate();
  const [showCart, setShowCart] = useState(false);
  const [searchFieldNueva, setSearchFieldNueva] = useState("");
  const [detailModal, setDetailModal] = useState(false);
  const [modalClient, setModalClient] = useState(null);
  const [, setNotification] = useState(null);
  const [cartAnimation, setCartAnimation] = useState(false);
  const [toast, setToast] = useState(null); // { text } — feedback transitorio (Figma)
  const [showClearConfirm, setShowClearConfirm] = useState(false); // confirmación "¿Vaciar el carrito?" (Figma)
  // Ubicaciones de stock (vista aérea): mapa id→{name,kind} para la etiqueta "desde X"
  // en el carrito. Regla de oro: la venta descuenta de donde está el producto.
  const [locById, setLocById] = useState({});
  // Desglose COMPLETO de stock por ubicación (incl. principal) para mostrarlo en cada
  // producto del grid: { product_id(lower): [{location_id,name,kind,quantity}] }.
  const [stockByLocProduct, setStockByLocProduct] = useState({});
  // Ubicaciones de venta por producto (no-principal con stock): { productId(lower): [{location_id,name,kind,quantity}] }
  const [saleLocsByProduct, setSaleLocsByProduct] = useState({});
  // Mini-diálogo "¿de dónde sale?": se abre SOLO cuando el backend devuelve 409
  // location_choice_required (ambigüedad: producto en 2+ neveras, o falta stock).
  // { detail, productId } — productId es a qué línea del carrito asignar la elección.
  const [locationChoice, setLocationChoice] = useState(null);
  const [creditModal, setCreditModal] = useState(false);
  const [creditUserId, setCreditUserId] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);
  const [purchaseSuccessData, setPurchaseSuccessData] = useState(null);

  // El aviso de éxito se desvanece solo: sin OK que pulsar, la pantalla nunca
  // se bloquea y la validación queda en un único paso (el modal de confirmación).
  useEffect(() => {
    if (!showPurchaseSuccess) return undefined;
    const t = setTimeout(() => {
      setShowPurchaseSuccess(false);
      setPurchaseSuccessData(null);
    }, 1500);
    return () => clearTimeout(t);
  }, [showPurchaseSuccess]);
  const [showPurchaseConfirm, setShowPurchaseConfirm] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [pendingClientSearch, setPendingClientSearch] = useState("");
  // Productos = same flow as Dispensario but only non-dispensario products (e.g. cola); Dispensario = only dispensario (weed). By law they cannot be mixed.
  const isProductosMode =
    embedProductosInControl || new URLSearchParams(location.search).get('mode') === 'productos';
  const cartType = isProductosMode ? 'productos' : 'dispensario';
  // Huella del cobro: qué se cobra y a quién. Cambia → clave nueva. Un refetch que
  // repinta las mismas líneas (mismo contenido, otro array) NO la cambia.
  const huellaCobro = useMemo(
    () => JSON.stringify([
      selectedClient?.id ?? null,
      cartType,
      cart.map((i) => [i.product_id || i.id, i.quantity, i.unit_price ?? i.price,
        i.real_weight ?? null, i.location_id ?? null, i.manual_discount ?? null]),
    ]),
    [cart, selectedClient?.id, cartType],
  );
  useEffect(() => {
    // La clave sobrevive a una recarga (la reacción natural ante un cobro «colgado»):
    // se guarda junto a su huella y, si al volver el carrito es el mismo, se reutiliza.
    //
    // CONGELADA mientras el modal de cobro está abierto. La huella incluye el peso real
    // y la ubicación de cada línea, así que cualquier cosa que los mueva con el cobro en
    // marcha —repesar tras un intento que se coló, la ubicación que el backend elige al
    // contestar, un refresco del borrador— acuñaba clave NUEVA: el reintento dejaba de
    // ser un reintento y cobraba otra vez. Es la hipótesis que queda viva del doble de
    // Kong del 24-09-2026, y taparla no cuesta nada aunque resulte no ser la causa.
    if (showPurchaseConfirm) {
      // Congelada, pero RE-ANCLADA a la huella viva: si el carrito cambia por debajo
      // (la ubicación que elige el backend al contestar, un refresco del borrador), lo
      // guardado seguiría apuntando a la huella vieja y al cerrar el modal no casaría —
      // se acuñaría clave nueva y el reintento cobraría otra vez. Congelar sin
      // re-anclar es peor que no congelar.
      fijarClaveCobro(venueID, huellaCobro, claveCobroRef.current);
      return;
    }
    claveCobroRef.current = claveCobroParaHuella(venueID, huellaCobro);
  }, [venueID, huellaCobro, showPurchaseConfirm]);
  const memberDiscount = selectedClient
    ? resolveMemberDiscount(selectedClient.profile, isProductosMode)
    : { rate: 0, label: null };
  const memberDiscountRate = memberDiscount.rate;
  const memberDiscountLabel = memberDiscount.label;
  const pendingCartStorageKey = venueID ? `pendingCart_${venueID}_${cartType}` : null;

  const navigateDispenserBase = useCallback(() => {
    navigate(isProductosMode ? '/dispenser?mode=productos' : '/dispenser', { replace: true });
  }, [isProductosMode, navigate]);

  const clearSelectedClientOnPage = useCallback((opts = {}) => {
    const { clearSearch = false } = opts;
    setSelectedClient(null);
    localStorage.removeItem('selectedClientId');
    setCart([]);
    if (clearSearch) setSearchFieldNueva('');
    if (embedProductosInControl) {
      onEntriesEmbedClearClient?.();
      return;
    }
    navigateDispenserBase();
  }, [embedProductosInControl, navigateDispenserBase, onEntriesEmbedClearClient]);
  // When switching Dispensario <-> Productos, clear cart and don't sync the wrong cart type (same component, pathname change)
  const justSwitchedCartTypeRef = useRef(false);
  // Ref para no sobrescribir con recargas tras cerrar el modal de edición (evita alternancia entre dos socios)
  const justEditedClientIdRef = useRef(null);
  // Ref para no sobrescribir selectedClient justo después de añadir crédito (evita que el banner vuelva al saldo viejo)
  const justAddedCreditIdRef = useRef(null);
  // Override de saldo en banner: cuando se añade crédito, mostramos este valor hasta que se cambie de socio (no depende de recargas)
  // El crédito mostrado en cabecera/carrito es el credit_balance REAL (igual que
  // "editar"). Este override solo se mantiene escrito por compatibilidad; ya no se
  // lee para mostrar (la resta del carrito se ve solo dentro del carrito).
  const [, setDisplayCreditOverride] = useState({ userId: null, balance: null });
  // Club social / Productos: base de saldo para "Disponible" = saldo - carrito
  const creditBaseForCartOverrideRef = useRef({ userId: null, baseBalance: null });

  // Limpiar override de crédito cuando no hay socio seleccionado
  useEffect(() => {
    if (!selectedClient?.id) {
      creditBaseForCartOverrideRef.current = { userId: null, baseBalance: null };
      setDisplayCreditOverride({ userId: null, balance: null });
    }
  }, [selectedClient?.id]);

  // Club social / Productos: mantener "Disponible" = baseBalance - total carrito mientras se edita el carrito
  useEffect(() => {
    if (!embedProductosInControl || !isProductosMode) return;
    if (!selectedClient?.id) return;
    const ref = creditBaseForCartOverrideRef.current;
    if (String(ref?.userId) !== String(selectedClient.id)) return;
    if (ref?.baseBalance == null) return;

    setDisplayCreditOverride({ userId: selectedClient.id, balance: null });
  }, [cart, selectedClient?.id, memberDiscountRate, embedProductosInControl, isProductosMode]);

  // Sincronizar modalClient cuando detailModal se abre (no sobrescribir si acabamos de editar)
  useEffect(() => {
    if (justEditedClientIdRef.current != null) return;
    if (detailModal && !modalClient && selectedClient) {
      setModalClient(selectedClient);
    }
  }, [detailModal, modalClient, selectedClient]);

  const {
    usersInVenue: usersInClub,
    // `cargada` = la sala ya ha contestado al menos una vez. Por defecto true
    // para que quien no la conozca (tests con el hook mockeado) vea la etiqueta
    // de siempre.
    cargada: salaCargada = true,
    refresh: refreshUsersInClub,
    updateUserCredit,
    updateUser: updateUserInClub,
  } = useInVenueUsers(token, venueID);

  const sociosEnSalaForPendingCart = entriesPanelUsersInClub ?? usersInClub;

  // Load pending cart from localStorage (club social productos)
  useEffect(() => {
    if (!embedProductosInControl) return;
    if (!isProductosMode) return;
    if (!pendingCartStorageKey) return;
    try {
      const stored = localStorage.getItem(pendingCartStorageKey);
      const parsed = stored ? JSON.parse(stored) : [];
      if (Array.isArray(parsed)) setPendingCartRaw(parsed);
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedProductosInControl, isProductosMode, pendingCartStorageKey]);

  // Persist pending cart
  useEffect(() => {
    if (!embedProductosInControl) return;
    if (!isProductosMode) return;
    if (!pendingCartStorageKey) return;
    try {
      if (!pendingCartRaw || pendingCartRaw.length === 0) {
        localStorage.removeItem(pendingCartStorageKey);
      } else {
        localStorage.setItem(pendingCartStorageKey, JSON.stringify(pendingCartRaw));
      }
    } catch {
      // ignore
    }
  }, [pendingCartRaw, embedProductosInControl, isProductosMode, pendingCartStorageKey]);
  

  const fetchClients = useCallback(async () => {
    // Sin club activo NO se pide: axios descarta un parámetro `undefined`, y la petición saldría sin
    // club — justo la que devuelve los socios de TODOS los clubes a un superadmin o a un empleado de
    // dos. El efecto vuelve a pedir cuando `venueID` llega (deps de este callback).
    if (!venueID) return [];
    try {
      const response = await axios.get("/v1/clients", {
        // `venue_id` y no `venueID`: el servidor solo lee el primero. Con el nombre equivocado
        // descartaba el club, y un empleado de dos clubes (o un superadmin) recibía los socios de
        // TODOS (05-10-2026; candado: DispensarioPideSociosDelClub.wave5).
        params: { venue_id: venueID },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setErrorMessage("");
      return response.data || [];
    } catch (error) {
      console.error("Fetch error:", error);
      const errorMsg = error.response?.data?.detail || "Error al cargar clientes";
      setErrorMessage(errorMsg);
      return [];
    }
  }, [token, venueID]);

  const fetchStaff = useCallback(async () => {
    try {
      const url = venueID ? `/v1/staff?venue_id=${venueID}` : '/v1/staff';
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data || [];
      console.log('📥 [Dispenser] Fetched staff data:', data);
      console.log('📥 [Dispenser] First staff member profile:', data[0]?.profile);
      console.log('📥 [Dispenser] First staff member documents_path:', data[0]?.profile?.documents_path);
      return data;
    } catch (error) {
      console.error("Fetch staff error:", error);
      return [];
    }
  }, [token, venueID]);

  const fetchOwners = useCallback(async () => {
    try {
      const url = venueID ? `/v1/owners?venue_id=${venueID}` : '/v1/owners';
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data || [];
      console.log('📥 [Dispenser] Fetched owners data:', data);
      console.log('📥 [Dispenser] First owner profile:', data[0]?.profile);
      console.log('📥 [Dispenser] First owner documents_path:', data[0]?.profile?.documents_path);
      return data;
    } catch (error) {
      console.error("Fetch owners error:", error);
      return [];
    }
  }, [token, venueID]);

  // Callbacks del modal - deben estar después de fetchClients
  const handleModalEdit = useCallback(() => {
    if (!modalClient?.id) return;
    const clientId = modalClient.id;
    // Si estamos en Dispensario o Productos (misma app de retirada), abrir modal local de edición
    if (location.pathname.includes('/dispenser') || embedProductosInControl) {
      setEditUserId(clientId);
      setEditModal(true);
      // No cerrar el detailModal aquí, se cerrará cuando se cierre el modal de edición
    } else {
      // Si estamos en otra página, navegar a customers
      setDetailModal(false);
      setTimeout(() => {
        setModalClient(null);
        navigate(`/customers?userId=${clientId}&edit=true`);
      }, 100);
    }
  }, [modalClient?.id, navigate, location.pathname, embedProductosInControl]);

  // Sin `window.confirm`: la ficha (ClientDetailModal) ya preguntó con su
  // ConfirmDialog antes de llamar aquí. El segundo aviso, nativo, se tragaba en
  // las tablets del club y el borrado que el operario acababa de confirmar no
  // ocurría (auditoría de UI, UI-003).
  const handleModalDelete = useCallback(async () => {
    if (!modalClient?.id) return;
    try {
      const response = await fetch(`/v1/users/${modalClient.id}`, { 
        method: 'DELETE', 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setDetailModal(false);
        setSelectedClient(null);
        localStorage.removeItem("selectedClientId");
        await fetchClients();
        setNotification({
          message: 'Cliente eliminado correctamente',
          type: 'success'
        });
        setTimeout(() => {
          setModalClient(null);
          setNotification(null);
        }, 100);
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.detail || 'Error al eliminar el cliente');
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      setErrorMessage('Error al eliminar el cliente');
    }
  }, [modalClient?.id, token, fetchClients]);

  const handleModalAddCredit = useCallback(() => {
    if (!modalClient?.id) return;
    // Si estamos en Dispensario o Productos (misma app de retirada), abrir modal local de crédito
    if (location.pathname.includes('/dispenser') || embedProductosInControl) {
      setCreditUserId(modalClient.id);
      setCreditModal(true);
      // No cerrar el detailModal aquí, se cerrará cuando se cierre el modal de crédito
    } else {
      // Si estamos en otra página, navegar a customers
      const clientId = modalClient.id;
      setDetailModal(false);
      setTimeout(() => {
        setModalClient(null);
        navigate(`/customers?userId=${clientId}&addCredit=true`);
      }, 100);
    }
  }, [modalClient?.id, navigate, location.pathname, embedProductosInControl]);
  
  

  // La sala NO se pide aquí al montar: el provider (Layout) ya la tiene y la
  // refresca al cambiar de club. Pedirla otra vez era una de las peticiones
  // dobles del atasco de Kong (20-09-2026). Tras cobrar/entrar sí se refresca.
  useEffect(() => {
    fetchClients().then(setClients);
  }, [fetchClients]);

  // Recargar selectedClient cuando cambia el venueID para obtener la información completa
  useEffect(() => {
    // Si el usuario seleccionó otro socio, quitar la protección
    if (justEditedClientIdRef.current !== null && selectedClient?.id !== justEditedClientIdRef.current) {
      justEditedClientIdRef.current = null;
    }
    // No recargar si acabamos de añadir crédito a este cliente (mantener selección y saldo; el ref se limpia en el efecto de profile)
    if (justAddedCreditIdRef.current != null && String(selectedClient?.id) === String(justAddedCreditIdRef.current)) {
      return;
    }
    // No recargar si el cambio viene de haber cerrado el modal de edición (evita alternancia)
    if (justEditedClientIdRef.current !== null && selectedClient?.id === justEditedClientIdRef.current) {
      return;
    }
    if (selectedClient?.id && venueID) {
      // Only drop the selection when the member clearly belongs to a DIFFERENT
      // venue (the venue was switched). Decide by venue_id — never by absence
      // from the paginated /v1/clients page, which would wrongly clear a socio
      // who is simply on a later page (the "socio disappears after a few seconds"
      // bug; staff/owner lists are small so they were never affected).
      if (selectedClient.venue_id && String(selectedClient.venue_id) !== String(venueID)) {
        setSelectedClient(null);
        localStorage.removeItem("selectedClientId");
        return;
      }
      // Same venue: refresh the member's data if they appear in the loaded
      // lists, but KEEP the current selection if they don't (pagination).
      // Se relee del SERVIDOR a propósito (no de la caché de sala): la línea de
      // coste o el descuento editados en Socios no llegan a esa caché y entran
      // en el precio del cobro (code review 20-09-2026).
      const reloadSelectedClient = async () => {
        try {
          const updatedClients = await fetchClients();
          const client = updatedClients.find((c) => c.id === selectedClient.id);
          if (client) {
            setSelectedClient(client);
            return;
          }
          const [staffList, ownersList] = await Promise.all([fetchStaff(), fetchOwners()]);
          const staffMember = staffList.find((s) => s.id === selectedClient.id);
          if (staffMember) {
            setSelectedClient(staffMember);
            return;
          }
          const owner = ownersList.find((o) => o.id === selectedClient.id);
          if (owner) {
            setSelectedClient(owner);
            return;
          }
          // Not on the loaded page but same venue → keep the current selection.
        } catch (error) {
          console.error("Error reloading selected client:", error);
        }
      };
      reloadSelectedClient();
    }
  }, [venueID, fetchClients, fetchStaff, fetchOwners, selectedClient?.id, selectedClient?.venue_id]);

  // Limpiar selectedClient cuando se sale de la página Retirada
  useEffect(() => {
    // Limpiar cuando el componente se desmonta (se sale de la página)
    return () => {
      setSelectedClient(null);
      localStorage.removeItem("selectedClientId");
      setCart([]);
      setSearchFieldNueva("");
    };
  }, [location.pathname]);

  const prevEntriesPanelUserIdRef = useRef(undefined);

  // Entradas (club social): sincronizar con el panel derecho sin pisar un socio elegido solo por el buscador interno
  useEffect(() => {
    if (!embedProductosInControl) return undefined;
    const prevPanelUserId = prevEntriesPanelUserIdRef.current;
    prevEntriesPanelUserIdRef.current = entriesPanelUserId;
    let cancelled = false;
    const panelCleared =
      (entriesPanelUserId == null || entriesPanelUserId === '') &&
      prevPanelUserId != null &&
      prevPanelUserId !== '';
    if (panelCleared) {
      (async () => {
        // Guardar borrador antes de soltar el socio: evita perder el carrito y un DELETE accidental
        if (debouncedSyncRef.current) {
          clearTimeout(debouncedSyncRef.current);
          debouncedSyncRef.current = null;
        }
        const clientId = selectedClientRef.current?.id;
        const items = cartRef.current;
        if (clientId && items?.length > 0) {
          await syncDraftCartRef.current?.(items);
          if (!cancelled) onEmbeddedDraftCartSynced?.();
        }
        if (cancelled) return;
        setSelectedClient(null);
        localStorage.removeItem("selectedClientId");
        setCart([]);
        setSearchFieldNueva("");
      })();
      return () => { cancelled = true; };
    }
    if (entriesPanelSyncClient?.id) {
      setSelectedClient(entriesPanelSyncClient);
      localStorage.setItem("selectedClientId", entriesPanelSyncClient.id);
      setSearchFieldNueva("");
    }
    return undefined;
  }, [embedProductosInControl, entriesPanelUserId, entriesPanelSyncClient, onEmbeddedDraftCartSynced]);

  // Cargar cliente desde localStorage solo si hay userId en la URL
  useEffect(() => {
    if (embedProductosInControl) return;
    if (justEditedClientIdRef.current != null) return;
    if (justAddedCreditIdRef.current != null) return;
    const searchParams = new URLSearchParams(location.search);
    const userId = searchParams.get("userId");
    if (!userId) {
      setSelectedClient(null);
      localStorage.removeItem("selectedClientId");
    }
  }, [clients, location.search, embedProductosInControl]);



  // Auto-select client if a userId is in the URL
  useEffect(() => {
    if (embedProductosInControl) return;
    // No sobrescribir si acabamos de cerrar el modal de edición (evita alternancia entre dos socios)
    if (justEditedClientIdRef.current != null) return;
    // No sobrescribir si acabamos de añadir crédito (mantener al que recibió el crédito)
    if (justAddedCreditIdRef.current != null) return;
    const searchParams = new URLSearchParams(location.search);
    const userId = searchParams.get("userId");
    if (userId) {
      // Buscar el cliente en las listas disponibles
      if (userId) {
        // Primero buscar en clients
        if (clients.length > 0) {
          const client = clients.find((c) => c.id === userId || c.id?.toString() === userId);
          if (client) {
            setSelectedClient(client);
            localStorage.setItem("selectedClientId", client.id);
            setSearchFieldNueva("");
            return;
          }
        }
        // Si no está en clients, buscar en usersInClub
        if (usersInClub.length > 0) {
          const user = usersInClub.find((u) => u.id === userId || u.id?.toString() === userId);
          if (user) {
            setSelectedClient(user);
            localStorage.setItem("selectedClientId", user.id);
            setSearchFieldNueva("");
            return;
          }
        }
        // Si no está en ninguna lista, buscar en staff y owners
        Promise.all([fetchStaff(), fetchOwners()]).then(([staffList, ownersList]) => {
          console.log('🔍 [Dispenser] Staff list length:', staffList.length);
          console.log('🔍 [Dispenser] Owners list length:', ownersList.length);
          console.log('🔍 [Dispenser] Looking for userId:', userId, 'type:', typeof userId);
          
          // Normalizar userId para comparación
          const normalizedUserId = String(userId).trim();
          
          // Buscar en staff - comparar IDs de forma más robusta
          const staffMember = staffList.find((s) => {
            if (!s || !s.id) return false;
            const staffId = String(s.id).trim();
            const match = staffId === normalizedUserId || s.id === userId;
            if (match) {
              console.log('🔍 [Dispenser] Found staff member:', s);
              console.log('🔍 [Dispenser] Staff member ID:', s.id, 'type:', typeof s.id);
              console.log('🔍 [Dispenser] Staff member profile:', s.profile);
              console.log('🔍 [Dispenser] Staff member profile type:', typeof s.profile);
              console.log('🔍 [Dispenser] Staff member documents_path:', s.profile?.documents_path);
              console.log('🔍 [Dispenser] Staff member documents_path type:', typeof s.profile?.documents_path);
            }
            return match;
          });
          
          if (staffMember) {
            console.log('✅ [Dispenser] Found staff member, setting as selectedClient');
            console.log('✅ [Dispenser] Staff member object:', staffMember);
            console.log('✅ [Dispenser] Staff member has profile?', !!staffMember.profile);
            console.log('✅ [Dispenser] Staff member profile:', staffMember.profile);
            console.log('✅ [Dispenser] Staff member documents_path:', staffMember.profile?.documents_path);
            console.log('✅ [Dispenser] Full staff member JSON:', JSON.stringify(staffMember, null, 2));
            
            // Asegurarse de que el objeto tenga la estructura correcta
            const staffToSet = {
              ...staffMember,
              profile: staffMember.profile ? { ...staffMember.profile } : null
            };
            
            console.log('✅ [Dispenser] Staff to set:', staffToSet);
            console.log('✅ [Dispenser] Staff to set profile:', staffToSet.profile);
            console.log('✅ [Dispenser] Staff to set documents_path:', staffToSet.profile?.documents_path);
            
            setSelectedClient(staffToSet);
            localStorage.setItem("selectedClientId", staffMember.id);
            setSearchFieldNueva("");
            return;
          }
          
          // Buscar en owners - comparar IDs de forma más robusta
          const owner = ownersList.find((o) => {
            if (!o || !o.id) return false;
            const ownerId = String(o.id).trim();
            const match = ownerId === normalizedUserId || o.id === userId;
            if (match) {
              console.log('🔍 [Dispenser] Found owner:', o);
              console.log('🔍 [Dispenser] Owner ID:', o.id, 'type:', typeof o.id);
              console.log('🔍 [Dispenser] Owner profile:', o.profile);
              console.log('🔍 [Dispenser] Owner profile type:', typeof o.profile);
              console.log('🔍 [Dispenser] Owner documents_path:', o.profile?.documents_path);
              console.log('🔍 [Dispenser] Owner documents_path type:', typeof o.profile?.documents_path);
            }
            return match;
          });
          
          if (owner) {
            console.log('✅ [Dispenser] Found owner, setting as selectedClient');
            console.log('✅ [Dispenser] Owner object:', owner);
            console.log('✅ [Dispenser] Owner has profile?', !!owner.profile);
            console.log('✅ [Dispenser] Owner profile:', owner.profile);
            console.log('✅ [Dispenser] Owner documents_path:', owner.profile?.documents_path);
            console.log('✅ [Dispenser] Full owner JSON:', JSON.stringify(owner, null, 2));
            
            // Asegurarse de que el objeto tenga la estructura correcta
            const ownerToSet = {
              ...owner,
              profile: owner.profile ? { ...owner.profile } : null
            };
            
            console.log('✅ [Dispenser] Owner to set:', ownerToSet);
            console.log('✅ [Dispenser] Owner to set profile:', ownerToSet.profile);
            console.log('✅ [Dispenser] Owner to set documents_path:', ownerToSet.profile?.documents_path);
            
            setSelectedClient(ownerToSet);
            localStorage.setItem("selectedClientId", owner.id);
            setSearchFieldNueva("");
            return;
          }
          
          console.log('❌ [Dispenser] User not found in staff or owners lists');
          // Si aún no se encuentra, recargar clients y buscar de nuevo
          fetchClients().then(updatedClients => {
            const client = updatedClients.find((c) => c.id === userId || c.id?.toString() === userId);
            if (client) {
              setSelectedClient(client);
              localStorage.setItem("selectedClientId", client.id);
              setSearchFieldNueva("");
            }
          });
        });
      }
    }
  }, [location.search, clients, usersInClub, token, fetchClients, fetchStaff, fetchOwners, embedProductosInControl]);

  // Debug: Verificar selectedClient cuando cambia y recargar si no tiene profile completo
  useEffect(() => {
    // No recargar si acabamos de aplicar el resultado de editar (evita alternancia)
    if (justEditedClientIdRef.current !== null && selectedClient?.id === justEditedClientIdRef.current) {
      return;
    }
    // No recargar si acabamos de añadir crédito (evita que el banner pierda el nuevo saldo)
    if (justAddedCreditIdRef.current != null && String(selectedClient?.id) === String(justAddedCreditIdRef.current)) {
      justAddedCreditIdRef.current = null;
      return;
    }
    if (selectedClient && selectedClient.id) {
      console.log('🔄 [Dispenser] selectedClient changed:', selectedClient);
      console.log('🔄 [Dispenser] selectedClient.id:', selectedClient.id);
      console.log('🔄 [Dispenser] selectedClient.profile:', selectedClient.profile);
      console.log('🔄 [Dispenser] selectedClient.profile?.documents_path:', selectedClient.profile?.documents_path);
      
      // Si el objeto no tiene profile o no tiene documents_path, intentar recargarlo
      if (!selectedClient.profile || !selectedClient.profile.documents_path) {
        console.warn('⚠️ [Dispenser] selectedClient missing profile or documents_path, attempting to reload...');
        
        // Intentar recargar desde staff u owners
        const reloadUser = async () => {
          try {
            const [staffList, ownersList] = await Promise.all([fetchStaff(), fetchOwners()]);
            
            // Buscar en staff
            const staffMember = staffList.find((s) => s.id === selectedClient.id || String(s.id) === String(selectedClient.id));
            if (staffMember && staffMember.profile && staffMember.profile.documents_path) {
              console.log('✅ [Dispenser] Reloaded staff member with profile:', staffMember);
              setSelectedClient(staffMember);
              return;
            }
            
            // Buscar en owners
            const owner = ownersList.find((o) => o.id === selectedClient.id || String(o.id) === String(selectedClient.id));
            if (owner && owner.profile && owner.profile.documents_path) {
              console.log('✅ [Dispenser] Reloaded owner with profile:', owner);
              setSelectedClient(owner);
              return;
            }
            
            console.warn('⚠️ [Dispenser] Could not reload user with profile');
          } catch (error) {
            console.error('❌ [Dispenser] Error reloading user:', error);
          }
        };
        
        reloadUser();
      }
    }
  }, [selectedClient, fetchStaff, fetchOwners]);

  const isLoadingDraftRef = useRef(false);
  // Procedencia de cada array de carrito. El efecto que decide si arma un guardado
  // completo no pregunta a banderas («¿acaba de llegar del servidor?», «¿acaba de
  // haber un toque?») sino al propio array que está mirando. Las banderas las consumía
  // el primer efecto que se ejecutara, aunque fuese el de OTRO carrito: cuando el
  // borrador llega del servidor fuera de un evento React pinta y deja su useEffect
  // pendiente; si un toque caía en ese hueco, el efecto del borrador se comía la
  // bandera del toque y el del toque, sin bandera, mandaba el carrito entero con una
  // versión vieja (CI de main del 18-09-2026: `INC, INC, POST base_version 0`).
  // Candado: ToqueTrasBorradorSinEfecto.wave7.
  //  · del servidor: GET del borrador o respuesta de un incremento. No se le devuelve
  //    (ida y vuelta inútil y carreras).
  const carritosDelServidorRef = useRef(new WeakSet());
  const syncChainRef = useRef(Promise.resolve());
  const prevCartTypeRef = useRef(cartType);
  const productsRef = useRef(products);
  productsRef.current = products;
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const selectedClientRef = useRef(selectedClient);
  selectedClientRef.current = selectedClient;
  // Socio al que pertenece el carrito ACTUAL en memoria (cartRef). Se usa para no escribir
  // nunca el carrito de un socio en la ficha de otro (duplicación cruzada al cambiar de socio).
  const cartOwnerIdRef = useRef(null);
  // Plan A: versión del carrito leída del servidor. Se reenvía como base_version al
  // guardar (compare-and-swap). Si el servidor responde 409 (otra escritura lo cambió),
  // recargamos en vez de pisar.
  const cartVersionRef = useRef(0);
  // U4 · la última foto del carrito que ESTE cliente conoce del servidor (en
  // forma de líneas de guardado). Es la 'base' del merge a tres bandas: sin
  // ella no se puede saber qué cambió esta pestaña y qué cambió la otra.
  const baseServerCartRef = useRef([]);
  // U4 · un solo reintento de fusión por conflicto (si el reintento también
  // choca, cede el paso: recarga clásica y a empezar — nunca bucles).
  const mergeRetryRef = useRef(false);
  const fetchDraftVersionRef = useRef(0);
  //  · de un toque en «Añadir»: ya viajó como INCREMENTO (POST /v1/draft-cart/items,
  //    BE #625). El efecto tiene que dejarlo pasar, o volvería a mandar el carrito
  //    completo y pisaría a la otra pestaña — que es justo lo que el incremento arregla.
  const carritosDeToqueRef = useRef(new WeakSet());
  // Peticiones del carrito en cola o en vuelo (incrementos y guardados). Solo con
  // la cola vacía se repinta el carrito desde la respuesta de un incremento: si
  // hay más toques detrás, la respuesta del primero va «por detrás» de la pantalla
  // y repintarla haría retroceder al operario.
  const trabajoEnCadenaRef = useRef(0);
  // Backend sin `POST /v1/draft-cart/items` (un despliegue por detrás, o un rollback):
  // al primer 404/405 los toques vuelven al guardado completo de siempre para el resto
  // de la sesión, en vez de pagar dos peticiones por toque.
  const incrementosNoSoportadosRef = useRef(false);
  const carritoVistoPorElEfectoRef = useRef(cart);
  // Feedback del add: un ÚNICO timer de toast (el 1.er add no borra el aviso del 3.º) y un
  // contador para que el operario vea que van 3, no un mensaje genérico parpadeando.
  const toastTimerRef = useRef(null);
  const toastCountRef = useRef(null); // { id, n }
  const debouncedSyncRef = useRef(null);
  // #487 (punto 3): tras un 409 de versión, la recarga NO debe empezar
  // re-enviando cartRef (podría llevar líneas YA COBRADAS si la respuesta del
  // cobro aún no llegó — dos HTTP sin orden garantizado). La recarga del 409
  // es "trae la verdad del servidor", no "impón la mía".
  const skipNextFlushRef = useRef(false);
  const embedDraftSyncDebounceMs = embedProductosInControl ? 150 : 600;

  const flushPendingDraftSync = useCallback(async () => {
    if (debouncedSyncRef.current) {
      clearTimeout(debouncedSyncRef.current);
      debouncedSyncRef.current = null;
    }
    // Lo que hay en memoria es tal cual lo que dio el servidor: nada que subir.
    if (isLoadingDraftRef.current || carritosDelServidorRef.current.has(cartRef.current)) return;
    await syncChainRef.current;
    // Y otra vez tras la cadena: un incremento que estaba en vuelo (toque y «Cobrar»
    // seguidos) acaba de repintar el carrito con la respuesta del servidor; mandarlo
    // entero ahora sería un guardado de más que sube la versión para nada.
    if (carritosDelServidorRef.current.has(cartRef.current)) return;
    const clientId = selectedClientRef.current?.id;
    const items = cartRef.current;
    if (!clientId || !items?.length) return;
    // Al cambiar de socio (X→Y), la ref del socio salta a Y al instante pero cartRef aún
    // tiene los items de X. Si el carrito no pertenece al socio destino, NO lo escribimos
    // (evita que los items de X acaben en la ficha de Y).
    if (cartOwnerIdRef.current != null && String(cartOwnerIdRef.current) !== String(clientId)) return;
    await enqueueDraftSyncRef.current?.(items);
  }, []);

  // Fetch draft cart from server (single source of truth). When we have userId in URL we came from "user icon" — clear stale localStorage so we never merge with old cart.
  const fetchDraftCart = useCallback(async () => {
    if (!selectedClient?.id || !venueID || !token) return;
    await syncChainRef.current;
    // Tras cambiar Dispensario↔Productos el carrito ya se guardó en el tipo anterior.
    if (!justSwitchedCartTypeRef.current && !skipNextFlushRef.current) {
      await flushPendingDraftSync();
    }
    skipNextFlushRef.current = false;
    const storageKey = `cartItems_${selectedClient.id}_${cartType}`;
    const version = ++fetchDraftVersionRef.current;
    isLoadingDraftRef.current = true;
    setLocationChoice(null);  // cerrar cualquier popup "¿de dónde sale?" colgado al recargar
    // Foto del carrito ANTES de vaciarlo, para reponer el «Dto. %» tecleado
    // cuando el servidor calle (el legacy no lo guarda). Con su dueño: si el
    // carrito en memoria era de OTRO socio, no se hereda nada. Se toma aquí y
    // no tras el await porque el setCart([]) de abajo ya habrá vaciado cartRef
    // para cuando llegue la respuesta.
    const lineasPrevias = cartRef.current || [];
    const duenoPrevio = cartOwnerIdRef.current;
    setCart([]);
    // A partir de aquí el carrito en memoria pertenece a este socio (ya se hizo el flush del
    // anterior más arriba, con su propio dueño). Marca el propietario para los guards de sync.
    cartOwnerIdRef.current = selectedClient.id;
    cartVersionRef.current = 0;  // se refresca con la versión que devuelva el fetch
    baseServerCartRef.current = [];  // U4: base nueva para el socio nuevo
    // If we landed here with userId in URL (e.g. clicked user icon), clear both cart keys for this user so we never fall back to stale/doubled data when switching Dispensario ↔ Productos
    const hasUserIdInUrl = new URLSearchParams(location.search).get('userId');
    if (hasUserIdInUrl) {
      localStorage.removeItem(`cartItems_${selectedClient.id}_dispensario`);
      localStorage.removeItem(`cartItems_${selectedClient.id}_productos`);
    }
    try {
      const res = await fetch(
        `/v1/draft-cart?user_id=${selectedClient.id}&venue_id=${venueID}&cart_type=${cartType}`,
        { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
      );
      if (!res.ok) {
        if (version !== fetchDraftVersionRef.current) return;
        const stored = localStorage.getItem(storageKey);
        setCart(stored ? JSON.parse(stored) : []);
        return;
      }
      const data = await res.json();
      if (version !== fetchDraftVersionRef.current) return;
      cartVersionRef.current = Number(data?.version) || 0;  // Plan A: versión para el CAS
      // U4: esta ES la foto del servidor que este cliente conoce a partir de ahora.
      baseServerCartRef.current = baseDesdeServidor(data);
      if (data?.items?.length) {
        const mapped = lineasDesdeServidor(data, productsRef.current);
        // El «Dto. %» tecleado vive solo en memoria mientras el backend legacy
        // no tenga dónde guardarlo: si este reemplazo lo tirase (409, cambiar
        // de área, re-seleccionar socio), el cobro saldría por el precio
        // entero. Si el servidor trae descuento (Cart v2), el suyo manda.
        // Solo se conserva si el carrito previo era de ESTE socio; entre áreas
        // no hay colisión posible (dispensario y productos no comparten
        // referencias, por ley van en carritos separados).
        // `supports_line_discount` es el tercer estado: dice si ESTE servidor sabe
        // guardar el «Dto. %». Cuando lo sabe, un `discount_pct: null` significa «esta
        // línea no tiene descuento» y NO hay que reponer el local — si no, un descuento
        // que alguien acaba de quitar en otra tablet resucita aquí y se cobra de menos.
        const conservado =
          duenoPrevio != null && String(duenoPrevio) === String(selectedClient.id)
            ? conservarDescuentosManuales(mapped, lineasPrevias, !!data.supports_line_discount)
            : mapped;
        carritosDelServidorRef.current.add(conservado);
        // cartRef se asigna al renderizar: un flush que llegue antes tiene que ver ya
        // la foto del servidor, no el carrito anterior.
        cartRef.current = conservado;
        setCart(() => conservado);
        localStorage.setItem(storageKey, JSON.stringify(conservado));
      } else {
        const vacio = [];
        carritosDelServidorRef.current.add(vacio);
        cartRef.current = vacio;
        setCart(() => vacio);
        localStorage.removeItem(storageKey);
      }
    } catch {
      if (version !== fetchDraftVersionRef.current) return;
      const stored = localStorage.getItem(storageKey);
      setCart(stored ? JSON.parse(stored) : []);
    } finally {
      isLoadingDraftRef.current = false;
      justSwitchedCartTypeRef.current = false;
    }
  }, [selectedClient?.id, venueID, token, cartType, location.search, flushPendingDraftSync]);
  // Ref para poder llamar a fetchDraftCart desde el sync (recarga tras un 409 de versión).
  const fetchDraftCartRef = useRef(null);
  fetchDraftCartRef.current = fetchDraftCart;

  // Cargar (solo lectura) el carrito del OTRO área del mismo socio, para que el panel
  // lateral muestre "todo": barra + dispensario. No es editable ni entra en el cobro
  // del área actual (la barra y el dispensario se cobran por separado / "Pagar Todo").
  const otherCartType = isProductosMode ? 'dispensario' : 'productos';
  useEffect(() => {
    let cancelled = false;
    async function loadOther() {
      if (!selectedClient?.id || !venueID || !token) { setOtherAreaItems([]); return; }
      try {
        const res = await fetch(
          `/v1/draft-cart?user_id=${selectedClient.id}&venue_id=${venueID}&cart_type=${otherCartType}`,
          { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
        );
        if (!res.ok) { if (!cancelled) setOtherAreaItems([]); return; }
        const data = await res.json();
        // Misma reconstrucción que el área activa: la copia que había aquí no traía el
        // «Dto. %» y el subtotal de solo lectura salía sin descuentos.
        if (!cancelled) setOtherAreaItems(lineasDesdeServidor(data, productsRef.current));
      } catch {
        if (!cancelled) setOtherAreaItems([]);
      }
    }
    loadOther();
    return () => { cancelled = true; };
    // Refresca al cambiar de socio, de área (cartType), o al abrir el panel.
  }, [selectedClient?.id, venueID, token, otherCartType, showCart]);

  // Auto-sanado del 409 sin salida: un reintento POR PRODUCTO (evita bucles). Se limpia en
  // cuanto un POST del carrito tiene éxito (el carrito ya es consistente).
  const locationHealRef = useRef({});

  // Sync cart to server (debounced). Empty cart = DELETE so it does not appear in Carritos; otherwise POST.
  // typeOverride: al cambiar de área, guardar el carrito en el tipo ANTERIOR (no en el nuevo).
  const performSyncDraftCart = useCallback(
    async (cartItems, typeOverride = null) => {
      const client = selectedClientRef.current;
      if (!client?.id || !venueID || !token) return;
      // Guard de propietario: nunca postear el carrito bajo un socio distinto al dueño real
      // del carrito en memoria (defensa en profundidad contra la carrera de cambio de socio).
      if (cartOwnerIdRef.current != null && String(cartOwnerIdRef.current) !== String(client.id)) return;
      const effectiveCartType = typeOverride ?? cartType;
      const raw = cartItems || [];
      // Agrupar por (producto + ubicación): cada nevera es un item de draft separado.
      const byLine = {};
      for (const item of raw) {
        const rawId = item.id;
        const loc = item.location_id ?? null;
        const key = `${(rawId == null ? '' : String(rawId)).toLowerCase()}|${loc ?? ''}`;
        const qty = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        if (qty <= 0) continue;
        const rw = item.real_weight != null ? Number(item.real_weight) : null;
        if (!byLine[key]) {
          byLine[key] = {
            product_id: rawId, quantity: qty, unit_price: price, real_weight: rw, location_id: loc,
            // El «Dto. %» viaja con la línea. El backend legacy lo acepta y lo
            // tira (transaction_items no tiene columna); Cart v2 lo PERSISTE
            // (cart_items.discount_pct, PR #486). null explícito para que
            // quitar el descuento también se propague.
            discount_pct: item.manual_discount != null ? Number(item.manual_discount) : null,
          };
        } else {
          byLine[key].quantity = Number((byLine[key].quantity + qty).toFixed(3));
          if (rw != null) byLine[key].real_weight = Number(((byLine[key].real_weight || 0) + rw).toFixed(3));
        }
      }
      const items = Object.values(byLine);
      try {
        if (items.length === 0) {
          const delRes = await fetch(
            `/v1/draft-cart?user_id=${client.id}&venue_id=${venueID}&cart_type=${effectiveCartType}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
          );
          // Sin carrito no hay versión: el siguiente toque crea uno nuevo en vez de
          // exigir (expected_version) un carrito que ya no existe.
          cartVersionRef.current = 0;
          baseServerCartRef.current = [];
          // El carrito reserva stock; al vaciar se devuelve → refrescar las cards
          fetchProductsRef.current?.(); fetchBoardRef.current?.();
          if (delRes.ok && embedProductosInControl) onEmbeddedDraftCartSynced?.();
          return;
        }
        const res = await fetch('/v1/draft-cart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_id: client.id,
            venue_id: venueID,
            cart_type: effectiveCartType,
            items,
            base_version: cartVersionRef.current,  // Plan A: compare-and-swap
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const detail = err?.detail;
          // Plan A — 409 de conflicto de versión: otra escritura cambió el carrito. NO
          // pisamos: recargamos el estado real del servidor y avisamos suave.
          if (res.status === 409 && detail && typeof detail === 'object' && detail.code === 'cart_version_conflict') {
            // U4 · FUSIONAR, no descartar: otra pestaña escribió — se lee su
            // verdad, se le aplica MI delta (fusionarCarrito) y se reintenta con
            // su versión. 3 toques míos + 3 suyos = 6, nadie pierde trabajo.
            if (!mergeRetryRef.current) {
              mergeRetryRef.current = true;
              try {
                const vres = await fetch(
                  `/v1/draft-cart?user_id=${client.id}&venue_id=${venueID}&cart_type=${effectiveCartType}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                const servidor = vres.ok ? await vres.json() : null;
                const fusionado = fusionarCarrito(
                  baseServerCartRef.current, items, baseDesdeServidor(servidor),
                );
                const reres = await fetch('/v1/draft-cart', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({
                    user_id: client.id, venue_id: venueID, cart_type: effectiveCartType,
                    items: fusionado, base_version: Number(servidor?.version) || 0,
                  }),
                });
                if (reres.ok) {
                  const saved2 = await reres.json().catch(() => null);
                  if (saved2?.version != null) cartVersionRef.current = Number(saved2.version);
                  baseServerCartRef.current = fusionado;
                  mergeRetryRef.current = false;
                  setToast({ text: 'Carrito fusionado con otra pestaña' });
                  setTimeout(() => setToast(null), 1800);
                  // Rehidratar la UI desde la verdad recién fusionada (sin re-flush).
                  skipNextFlushRef.current = true;
                  fetchDraftCartRef.current?.();
                  return;
                }
              } catch { /* cae al camino clásico de abajo */ }
            }
            // Segundo choque seguido (o la fusión falló): el camino clásico —
            // recargar la verdad del servidor y que el operario revise.
            mergeRetryRef.current = false;
            if (typeof detail.current_version === 'number') cartVersionRef.current = detail.current_version;
            setToast({ text: 'Carrito actualizado, recargado' });
            setTimeout(() => setToast(null), 1800);
            // #487 (punto 3): recargar SIN flushear lo local — ver skipNextFlushRef.
            skipNextFlushRef.current = true;
            fetchDraftCartRef.current?.();
          } else if (res.status === 409 && detail && typeof detail === 'object' && detail.code === 'location_choice_required') {
            const pid = (detail.product_id == null ? '' : String(detail.product_id)).toLowerCase();
            const hasOptions = Array.isArray(detail.options) && detail.options.length > 0;
            if (!hasOptions) {
              // Callejón sin salida: el backend pide elegir pero NO ofrece ninguna ubicación
              // (Bug 2 — ubicación en negativo / producto sin desglose). Abrir un modal sin
              // botones cuelga el carrito. Auto-sanar: quitar location_id de las líneas de este
              // producto (lo que además FUSIONA la línea duplicada, porque el sync agrupa por
              // producto|ubicación) y reintentar UNA vez por producto. Esto sobrevive a un
              // backend viejo que aún mande options: [] (FE y BE se despliegan por separado).
              if (!locationHealRef.current[pid]) {
                locationHealRef.current[pid] = true;
                const healed = (cartRef.current || []).map((it) =>
                  (String(it.id) || '').toLowerCase() === pid ? { ...it, location_id: null } : it
                );
                setCart(healed);
                setToast({ text: 'Ubicación ajustada automáticamente' });
                setTimeout(() => setToast(null), 1800);
                enqueueDraftSyncRef.current?.(healed);
              } else {
                // Ya lo intentamos una vez y sigue sin salida: no insistir (evita bucles).
                setErrorMessage(`No se pudo asignar ubicación para ${detail.product_name || 'el producto'}`);
              }
            } else {
              // Producto en 2+ ubicaciones (ambigüedad) o falta stock en la designada →
              // abrir el mini-diálogo "¿de dónde sale?" para que el operario elija.
              const prod = (productsRef.current || []).find((p) => p && String(p.id).toLowerCase() === pid);
              setLocationChoice({ detail, productId: detail.product_id, unitType: prod?.unit_type });
            }
          } else {
            setErrorMessage((detail && typeof detail === 'string' ? detail : detail?.msg) || 'Error al guardar el carrito');
          }
        } else {
          setErrorMessage('');
          // El carrito quedó consistente: un futuro 409 sin salida es nuevo y merece su reintento.
          locationHealRef.current = {};
          // Plan A: actualizar la versión con la que devuelve el servidor (para el próximo CAS).
          const saved = await res.json().catch(() => null);
          if (saved && saved.version != null) cartVersionRef.current = Number(saved.version) || cartVersionRef.current;
          baseServerCartRef.current = items;      // U4: lo guardado es la nueva base
          mergeRetryRef.current = false;          // conflicto resuelto (si lo hubo)
          // El carrito reserva/ajusta stock → refrescar las cards para verlo al instante
          fetchProductsRef.current?.(); fetchBoardRef.current?.();
          if (embedProductosInControl) onEmbeddedDraftCartSynced?.();
        }
      } catch (e) {
        console.warn('Draft cart sync failed:', e);
      } finally {
        // Se avisa haya ido bien o mal: el contador del menú vuelve a preguntar por
        // la verdad, así que un aviso de más no miente — uno de menos, sí.
        avisarCarritosCambiados();
      }
    },
    [venueID, token, cartType, embedProductosInControl, onEmbeddedDraftCartSynced]
  );

  const enqueueDraftSync = useCallback(
    (cartItems, typeOverride = null) => {
      trabajoEnCadenaRef.current += 1;
      const run = () => performSyncDraftCart(cartItems, typeOverride)
        .finally(() => { trabajoEnCadenaRef.current -= 1; });
      const next = syncChainRef.current.then(run, run);
      syncChainRef.current = next.catch(() => {});
      return next;
    },
    [performSyncDraftCart]
  );

  const enqueueDraftSyncRef = useRef(enqueueDraftSync);
  enqueueDraftSyncRef.current = enqueueDraftSync;
  const syncDraftCart = enqueueDraftSync;
  const syncDraftCartRef = useRef(syncDraftCart);
  syncDraftCartRef.current = syncDraftCart;

  /** Aviso de larga duración con el temporizador único de toasts: así el «Añadido»
   *  de 1,8 s que se armó al tocar no se lo come, ni él se come al siguiente. */
  const avisar = useCallback((text, ms = 4000) => {
    setToast({ text });
    toastCountRef.current = null;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => { setToast(null); toastCountRef.current = null; }, ms);
  }, []);

  /**
   * Un toque en «Añadir» viaja como INCREMENTO: `POST /v1/draft-cart/items` con el
   * delta de ESA línea y una `Idempotency-Key` (BE #625). El servidor suma bajo su
   * cerrojo, así que dos pantallas sobre el mismo socio ya no se pisan (RF-001,
   * RF-002). Va encadenado en `syncChainRef` con los guardados completos para
   * conservar el orden de lo que hizo el operario.
   *
   * Invariante que lo sostiene: `cartVersionRef`/`baseServerCartRef` describen una
   * foto del servidor que la pantalla HA ABSORBIDO. Por eso:
   *  · éxito con la cola vacía y sin edición pendiente → se repinta desde la respuesta
   *    (misma forma que el GET, RF-008) y versión/base pasan a ser esa respuesta: así
   *    esta pantalla ve también lo que sumó la otra;
   *  · éxito con más trabajo detrás → NO se repinta (la respuesta va por detrás de la
   *    pantalla), la versión se queda como estaba y la base absorbe solo MI delta. El
   *    siguiente guardado completo chocará (409) y la fusión a tres bandas de la U4
   *    reconciliará con las líneas de la otra pestaña, en vez de pasar el CAS con una
   *    versión que la pantalla no conoce y borrarlas sin aviso;
   *  · 409 `cart_gone` → el carrito se cobró o se vació en otra pantalla: NO se
   *    resucita (RF-003, el fantasma del #487). Se recarga y se avisa claro;
   *  · red caída → hasta 3 intentos con la MISMA clave (RF-007) y tope de tiempo por
   *    intento; si no, se recarga la verdad y se avisa sin sonar a rutina (RF-004);
   *  · 404/405 → backend sin el endpoint: el toque y los siguientes van por el
   *    guardado completo de siempre;
   *  · cualquier otro error (stock, ubicación ambigua…) → lo resuelve el guardado
   *    completo, que ya sabe abrir el «¿de dónde sale?» y pintar el error.
   */
  const enviarIncremento = useCallback((toque) => {
    const clave = nuevaClaveIdempotencia();
    const clientId = selectedClientRef.current?.id;
    const cartTypeToque = cartType;
    const INTENTOS = 3;
    const PAUSAS_MS = [400, 1200];
    const TOPE_MS = 6000;
    const dormir = (ms) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      if (!clientId || !venueID || !token) return;
      // El borrador de este socio aún se está leyendo: esperar a que llegue, o el
      // incremento saldría con la versión de otro socio/área y su respuesta la pisaría
      // el GET al aterrizar. (Tope por si algo se atasca: nunca bloquear la cadena.)
      for (let i = 0; i < 200 && isLoadingDraftRef.current; i++) await dormir(50);
      if (cartOwnerIdRef.current != null && String(cartOwnerIdRef.current) !== String(clientId)) return;
      const fetchVisto = fetchDraftVersionRef.current;
      const body = {
        user_id: clientId,
        venue_id: venueID,
        cart_type: cartTypeToque,
        product_id: toque.id,
        location_id: toque.location_id ?? null,
        delta: toque.delta,
        unit_price: Number(toque.price) || 0,
        // null = «no traigo peso»: el servidor conserva el que tenga.
        real_weight: toque.real_weight ?? null,
      };
      // 0 = «esta pantalla no conoce ningún carrito»: se crea si no existe.
      if (cartVersionRef.current) body.expected_version = cartVersionRef.current;

      // Otro socio, o una recarga/vaciado/cobro por medio: la respuesta ya no es de esta pantalla.
      const sigueVigente = () =>
        fetchVisto === fetchDraftVersionRef.current &&
        String(selectedClientRef.current?.id ?? '') === String(clientId);

      let res = null;
      let intentos = 0;
      for (let i = 1; i <= INTENTOS; i++) {
        intentos = i;
        const corte = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const tope = corte ? setTimeout(() => corte.abort(), TOPE_MS) : null;
        try {
          res = await fetch('/v1/draft-cart/items', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'Idempotency-Key': clave,
            },
            body: JSON.stringify(body),
            signal: corte?.signal,
          });
          break;
        } catch (e) {
          if (i < INTENTOS && sigueVigente()) await dormir(PAUSAS_MS[i - 1]);
        } finally {
          if (tope) clearTimeout(tope);
        }
      }
      if (!res) {
        if (!sigueVigente()) return;
        console.warn('[carrito] incremento perdido', { socio: clientId, venue: venueID, intentos });
        avisar('No se pudo guardar el último toque: revisa las cantidades');
        // La pantalla enseña un toque que el servidor quizá no tiene: recargar la
        // verdad SIN flushear lo local (re-enviar cartRef sumaría el toque otra vez si
        // algún intento sí llegó — es lo que la clave de idempotencia evita).
        skipNextFlushRef.current = true;
        fetchDraftCartRef.current?.();
        return;
      }

      if (res.ok) {
        const data = await res.json().catch(() => null);
        if (!sigueVigente()) return;
        setErrorMessage('');
        locationHealRef.current = {};
        const colaVacia = trabajoEnCadenaRef.current <= 1 && !debouncedSyncRef.current;
        if (colaVacia) {
          cartVersionRef.current = Number(data?.version) || 0;
          baseServerCartRef.current = baseDesdeServidor(data);
          const lineas = lineasDesdeServidor(data, productsRef.current);
          const conservado = conservarDescuentosManuales(lineas, cartRef.current || [], !!data?.supports_line_discount);
          carritosDelServidorRef.current.add(conservado);
          // cartRef se asigna al renderizar: quien lea el carrito nada más soltarse la
          // cadena (el flush del cobro) tiene que ver YA lo repintado.
          cartRef.current = conservado;
          setCart(() => conservado);
          // Las cards y el contador del menú, una vez por ráfaga, no por toque.
          fetchProductsRef.current?.(); fetchBoardRef.current?.();
          if (embedProductosInControl) onEmbeddedDraftCartSynced?.();
          avisarCarritosCambiados();
        } else {
          baseServerCartRef.current = baseConDelta(baseServerCartRef.current, toque);
        }
        return;
      }

      const err = await res.json().catch(() => ({}));
      const detail = err?.detail;
      if (!sigueVigente()) return;
      if (res.status === 409 && detail && typeof detail === 'object' && detail.code === 'cart_gone') {
        cartVersionRef.current = 0;
        baseServerCartRef.current = [];
        avisar('Ese carrito ya se cobró o se vació: revisa antes de seguir');
        // Recargar SIN flushear lo local: re-enviar cartRef resucitaría la venta cobrada.
        skipNextFlushRef.current = true;
        fetchDraftCartRef.current?.();
        return;
      }
      if (res.status === 404 || res.status === 405) {
        if (!incrementosNoSoportadosRef.current) {
          incrementosNoSoportadosRef.current = true;
          console.warn('[carrito] el backend no tiene POST /v1/draft-cart/items: toques por guardado completo');
        }
        enqueueDraftSyncRef.current?.(cartRef.current);
        return;
      }
      // Stock, ubicación ambigua, socio borrado…: el guardado completo ya sabe qué
      // hacer con cada uno (modal «¿de dónde sale?», mensaje de error). Se encola
      // detrás de este incremento, con la versión que ya conocemos.
      enqueueDraftSyncRef.current?.(cartRef.current);
    };

    trabajoEnCadenaRef.current += 1;
    const tarea = () => run().finally(() => { trabajoEnCadenaRef.current -= 1; });
    const next = syncChainRef.current.then(tarea, tarea);
    syncChainRef.current = next.catch(() => {});
    return next;
  }, [venueID, token, cartType, embedProductosInControl, onEmbeddedDraftCartSynced, avisar]);
  const enviarIncrementoRef = useRef(enviarIncremento);
  enviarIncrementoRef.current = enviarIncremento;

  // useLayoutEffect: debe ejecutarse ANTES del useEffect que llama fetchDraftCart, para
  // marcar justSwitched y guardar en el tipo anterior sin que el fetch haga flush al nuevo.
  useLayoutEffect(() => {
    const prevType = prevCartTypeRef.current;
    if (prevType === cartType) return;

    const itemsToFlush = [...(cartRef.current || [])];
    const clientId = selectedClientRef.current?.id;
    justSwitchedCartTypeRef.current = true;
    prevCartTypeRef.current = cartType;
    setCart([]);

    if (debouncedSyncRef.current) {
      clearTimeout(debouncedSyncRef.current);
      debouncedSyncRef.current = null;
    }
    if (clientId && itemsToFlush.length > 0) {
      enqueueDraftSync(itemsToFlush, prevType);
    }
  }, [cartType, enqueueDraftSync]);

  useEffect(() => {
    if (selectedClient) fetchDraftCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on id only to avoid refetch on client ref change
  }, [selectedClient?.id, fetchDraftCart]);
  useEffect(() => {
    // Solo un CAMBIO del carrito arma un guardado. Cambiar de socio o de área re-ejecuta
    // este efecto con el mismo array: guardarlo mandaba un POST/DELETE fantasma 600 ms
    // después de elegir socio y, peor, contaba como «edición pendiente» para los
    // primeros toques (409 + fusión + recarga por nada).
    const cambioElCarrito = cart !== carritoVistoPorElEfectoRef.current;
    carritoVistoPorElEfectoRef.current = cart;
    if (!cambioElCarrito) return;
    if (!selectedClient) return;
    if (isLoadingDraftRef.current) return;
    if (justSwitchedCartTypeRef.current) return;
    // Lo que acaba de llegar del servidor no se le devuelve (ida y vuelta inútil y
    // carreras). Se pregunta al ARRAY, no a una bandera: así este efecto, aunque se
    // ejecute tarde, solo habla del carrito que está mirando (ver carritosDelServidorRef).
    if (carritosDelServidorRef.current.has(cart)) return;
    // Un toque ya viajó como incremento (BE #625): mandar además el carrito entero
    // volvería a pisar a la otra pestaña… salvo que hubiera un guardado PENDIENTE de
    // una edición anterior (la limpieza de este efecto acaba de cancelar su timer y la
    // ref aún guarda el id): entonces se re-arma, y ese guardado llevará también el
    // toque, detrás del incremento en la cadena.
    if (carritosDeToqueRef.current.has(cart) && !debouncedSyncRef.current) return;
    if (debouncedSyncRef.current) clearTimeout(debouncedSyncRef.current);
    debouncedSyncRef.current = setTimeout(() => {
      debouncedSyncRef.current = null;   // ya no hay guardado pendiente
      if (isLoadingDraftRef.current) return;
      if (justSwitchedCartTypeRef.current) return;
      enqueueDraftSyncRef.current(cartRef.current);
    }, embedDraftSyncDebounceMs);
    return () => {
      if (debouncedSyncRef.current) clearTimeout(debouncedSyncRef.current);
    };
    // Depend on selectedClient?.id (not the object): muchos efectos hacen
    // setSelectedClient(nuevoObjeto) con la MISMA id (recargas de saldo/socio), y con el
    // objeto en deps cada recreación re-disparaba un POST del carrito completo sin acción
    // del usuario ("hace cosas solo" / doble-add fantasma).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, selectedClient?.id, cartType, embedDraftSyncDebounceMs]);


  const scheduleDraftSync = useCallback((delayMs = embedDraftSyncDebounceMs) => {
    if (!selectedClientRef.current?.id) return;
    if (isLoadingDraftRef.current || justSwitchedCartTypeRef.current) return;
    if (debouncedSyncRef.current) clearTimeout(debouncedSyncRef.current);
    debouncedSyncRef.current = setTimeout(() => {
      debouncedSyncRef.current = null;
      if (isLoadingDraftRef.current) return;
      if (justSwitchedCartTypeRef.current) return;
      enqueueDraftSyncRef.current(cartRef.current);
    }, delayMs);
  }, [embedDraftSyncDebounceMs]);

  // Club social: al salir de la página o desmontar, persistir el borrador pendiente
  useEffect(() => {
    if (!embedProductosInControl) return undefined;
    const onPageHide = () => { flushPendingDraftSync(); };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      flushPendingDraftSync();
    };
  }, [embedProductosInControl, flushPendingDraftSync]);

  // Fallback: persist cart to localStorage (separate key per dispensario vs productos).
  // Skip during fetch loading to avoid writing [] when fetchDraftCart clears cart before server responds.
  useEffect(() => {
    if (selectedClient && !isLoadingDraftRef.current) {
      localStorage.setItem(`cartItems_${selectedClient.id}_${cartType}`, JSON.stringify(cart));
    }
  }, [cart, selectedClient, cartType]);

  // Secuencia de getTransactions: número de cada petición y el de la última que pintó.
  const txReqRef = useRef(0);
  const txPintadaRef = useRef(0);

  // Fetch transactions for the selected client
  const getTransactions = useCallback(async () => {
    const myReq = ++txReqRef.current;
    // SOLO las del socio seleccionado, y las 200 más recientes: pedir el club
    // entero aquí era el mayor devorador de tiempo de la app (p95 de 11 s y
    // ~10.000 llamadas/semana en prod, Sentry 20-ago) — y esta llamada se
    // dispara en CADA selección de socio. ClientTransactions ya filtraba por
    // clientId en el cliente, así que la vista no cambia; 200 cubre de sobra el
    // historial que se pinta y lo que se anula desde él.
    //
    // Sin socio NO se pide nada (Sentry 10/11-10: las 55 peticiones diarias de
    // más de 5 s eran todas `?venue_id=` a secas, del POS tras cada venta con
    // saldo: el efecto de «sin ?userId= en la URL» ya había soltado al socio y
    // esto caía al club entero). Sin socio ClientTransactions no lee la lista.
    const socioId = selectedClientRef.current?.id;
    if (!socioId) return;
    // Se pinta si es del socio que está en pantalla y es MÁS RECIENTE que lo último
    // pintado. No «solo la última pedida»: tras un −1u salen dos peticiones del mismo
    // socio, y si la segunda fallaba se tiraba la primera (buena) y el historial se
    // quedaba con la línea vieja y su −1u activo. La del socio anterior llegando tarde
    // no pinta nunca (otro socio).
    const sigueVigente = () =>
      myReq > txPintadaRef.current &&
      String(selectedClientRef.current?.id ?? '') === String(socioId);
    try {
      const response = await fetch(`/v1/get-all-transactions?user_id=${encodeURIComponent(socioId)}&limit=200${venueID ? `&venue_id=${encodeURIComponent(venueID)}` : ''}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${response.status}`);
      }
      const data = await response.json();
      if (!sigueVigente()) return;
      txPintadaRef.current = myReq;
      setTransactions(data.transactions || []);
    } catch (error) {
      console.warn("getTransactions failed:", error.message);
    }
  }, [token, venueID]);

  // Con socio pide su historial; sin socio no pide nada. La lista no se vacía al soltar:
  // ClientTransactions solo enseña las filas del socio en pantalla, y así al volver a
  // elegir al mismo no sale «sin movimientos» mientras carga (o para siempre si falla).
  useEffect(() => {
    getTransactions();
  }, [selectedClient, getTransactions]);

  // --- Fetch Products using Axios ---
  // Dispensario: only dispensario products. Productos: only non-dispensario (exclude dispensario) — same flow, cannot mix by law.
  const fetchProducts = useCallback(async () => {
    try {
      const PAGE_SIZE = 200;
      let skip = 0;
      let productsData = [];
      while (true) {
        const response = await axios.get('/v1/get-venue-products', {
          params: {
            ...(isProductosMode
              ? { exclude_dispensario: true }
              : { dispensario_only: true }),
            skip,
            limit: PAGE_SIZE,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Venue-ID": venueID,
          },
        });
        const batch = Array.isArray(response.data) ? response.data : [];
        productsData = productsData.concat(batch);
        if (batch.length < PAGE_SIZE) break;
        skip += PAGE_SIZE;
      }
      if (isProductosMode) {
        productsData = productsData.filter((p) => !p.is_dispensario);
      }
      // SCRUM-74: src estables aunque el backend firme URLs distintas por worker —
      // si el objeto de la imagen no cambió, se conserva la URL ya vista y el <img>
      // ni se entera del refetch.
      setProducts(withStableImageUrls(productsData));
      setLoadedProducts(true);
      setErrorMessage("");
    } catch (error) {
      console.error("Error fetching products:", error.response || error);
      const errorMsg = error.response?.data?.detail || "Error al cargar productos";
      setErrorMessage(errorMsg);
    }
  }, [token, venueID, isProductosMode]);

  // Ref para refrescar productos desde syncDraftCart (definido antes que fetchProducts)
  const fetchProductsRef = useRef(null);
  fetchProductsRef.current = fetchProducts;

  /** Una anulación devolvió saldo: POS, ficha y sala lo reflejan al momento. */
  const aplicarSaldoAnulado = useCallback((clientId, newBalance) => {
    const patch = (prev) =>
      prev && String(prev.id) === String(clientId)
        ? { ...prev, credit_balance: newBalance, profile: { ...(prev.profile || {}), credit_balance: newBalance } }
        : prev;
    setSelectedClient(patch);
    setModalClient(patch);
    setClients((prev) => prev.map(patch));
    updateUserCredit?.(clientId, newBalance);
  }, [updateUserCredit]);

  /** El historial del socio cambió (anulación parcial desde el cajón inferior).
   *
   *  Reporte del cliente (18-ago): "al restar unidad desde el historial... lo hace
   *  bien pero no se refleja al instante, sino al recargar". El cajón solo pedía las
   *  transacciones otra vez, así que la línea del historial sí cambiaba pero las dos
   *  cosas que el socio y el dependiente están mirando NO: el saldo en cabecera y el
   *  stock del surtido. El backend ya devolvía `new_balance` y aquí se tiraba a la
   *  basura, porque `getTransactions` no recibe argumentos.
   *
   *  El stock se pide de nuevo en vez de descontarlo a mano: la anulación puede
   *  devolver unidades o una línea al peso entera, y el servidor ya sabe el número
   *  bueno. */
  const historialCambiado = useCallback(async (data) => {
    const nb = data?.new_balance;
    if (nb != null && Number.isFinite(Number(nb)) && selectedClient?.id) {
      aplicarSaldoAnulado(selectedClient.id, Number(nb));
      // Bus de saldo (plan CRUO §E): la anulación devuelve dinero — se avisa igual.
      avisarSaldoCambiado(selectedClient.id, Number(nb));
    }
    await Promise.all([getTransactions(), fetchProducts()]);
  }, [aplicarSaldoAnulado, getTransactions, fetchProducts, selectedClient?.id]);

  // Secuencia de fetchBoard para descartar respuestas fuera de orden (respuesta stale gana).
  const boardReqRef = useRef(0);

  // Board de ubicaciones (vista aérea): mapa id→{name,kind} para la etiqueta del
  // carrito + ubicaciones de venta por producto (no-principal con stock) para
  // PREGUNTAR proactivamente en cada add cuando hay 2+ neveras. Tolerante a fallo:
  // si no carga, se cae al modo reactivo (409 del backend).
  const fetchBoard = useCallback(async () => {
    if (!token || !venueID) return;
    // Guardia de secuencia: solo la ÚLTIMA petición aplica su resultado. Sin esto, el
    // fetchBoard de montaje (reserva=0) puede resolver DESPUÉS del post-carrito y pisar el
    // disponible correcto con el físico stale (chip mostraba 59 en vez de 56).
    const myReq = ++boardReqRef.current;
    try {
      const res = await apiFetch(`/v1/stock/board?venue_id=${venueID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok || myReq !== boardReqRef.current) return;
      const data = await safeJson(res);
      if (myReq !== boardReqRef.current) return;
      const locs = {};
      // `dispensable` viaja con el resto: sin él, el filtro de candidatas ofrecía la roja.
      for (const l of data.locations || []) locs[String(l.id)] = { name: l.name, kind: l.kind, dispensable: l.dispensable };
      const byProd = {};
      const fullByProd = {};
      for (const p of data.products || []) {
        const opts = [];
        const full = [];
        // POS: mostrar DISPONIBLE por ubicación (físico − reservado en carritos). Cae al físico
        // (by_location) si el backend aún no manda available_by_location. Ver ARQUITECTURA_STOCK.md.
        for (const [locId, qty] of Object.entries(p.available_by_location || p.by_location || {})) {
          const meta = locs[String(locId)];
          const q = Number(qty) || 0;
          if (!meta || q === 0) continue;
          // Chip informativo: incluye ubicaciones en NEGATIVO (la UI las pinta con ⚠️).
          full.push({ location_id: String(locId), name: meta.name, kind: meta.kind, quantity: q });
          // Candidata de VENTA: la regla compartida (`esFuenteDeVenta`): de sala, en 🟢 y
          // con stock positivo. Una en negativo NO es fuente (Bug 2: el add se ancla, el
          // backend la rechaza y el carrito se cuelga); una en 🔴 no existe para vender.
          // El disponible negativo lo cubre el dispensario por su rama de backend
          // (armario en negativo), no el filtro de candidatas.
          if (esFuenteDeVenta(meta, q)) {
            opts.push({ location_id: String(locId), name: meta.name, kind: meta.kind, quantity: q });
          }
        }
        full.sort((a, b) => (a.kind === 'principal' ? -1 : b.kind === 'principal' ? 1 : a.name.localeCompare(b.name)));
        byProd[String(p.id).toLowerCase()] = opts;
        fullByProd[String(p.id).toLowerCase()] = full;
      }
      setLocById(locs);
      setSaleLocsByProduct(byProd);
      setStockByLocProduct(fullByProd);
    } catch { /* sin board → etiquetas/proactivo off, el 409 del backend cubre */ }
  }, [token, venueID]);

  const fetchBoardRef = useRef(null);
  fetchBoardRef.current = fetchBoard;

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  useEffect(() => {
    if (token && venueID) {
      setLoadedProducts(false);
      fetchProducts();
    }
  }, [token, venueID, fetchProducts]);

  // Update selected client from inline search and clear the search field so that dropdown is hidden
  const handleClientSelection = async (client) => {
    setDisplayCreditOverride({ userId: null, balance: null });
    let resolvedId = client?.id;
    let socioSeleccionado = client;
    // El aviso se decide AQUÍ, con el objeto de la búsqueda (ya trae notes/notes_popup):
    // ni espera al refetch (popup tardío en venues grandes) ni se pierde si falla la red.
    // Solo al CAMBIAR de socio — re-taps del ya seleccionado no re-disparan.
    // Embebido en Control: el aviso lo muestra Control (via onEmbedClientSelected);
    // dispararlo aquí también apilaba dos popups idénticos.
    const esNuevaSeleccion = String(client?.id) !== String(selectedClient?.id);
    if (!embedProductosInControl && esNuevaSeleccion && notaConAviso(client)) {
      setAvisoSocio(client);
    }
    // Buscar el cliente completo en la lista de clientes actualizada
    try {
      const updatedClients = await fetchClients();
      // Si no está en clients, usar el cliente que tenemos (puede ser de usersInClub)
      const socio = updatedClients.find((c) => c.id === client.id) || client;
      setSelectedClient(socio);
      localStorage.setItem("selectedClientId", socio.id);
      resolvedId = socio.id;
      socioSeleccionado = socio;
    } catch (error) {
      console.error("Error loading client:", error);
      // Si falla, usar el cliente que tenemos
      setSelectedClient(client);
      localStorage.setItem("selectedClientId", client.id);
      resolvedId = client?.id;
    }
    setSearchFieldNueva("");
    if (embedProductosInControl && resolvedId != null) {
      // Se pasa el objeto además del id: Control solo tiene la lista de sala, y un
      // socio buscado que aún no hizo check-in no aparecería en ella (aviso perdido).
      onEmbedClientSelected?.(resolvedId, socioSeleccionado);
    }
  };

  // Guarda anti clic-fantasma (módulo puro). Sustituye a la ventana de 350 ms que descartaba
  // clics rápidos buenos en silencio. Ver addClickGuard.js.
  const addGuardRef = useRef(null);
  if (addGuardRef.current === null) addGuardRef.current = createAddGuard();
  const cartAnimTimerRef = useRef(null);

  // Mete una línea en el carrito real, fijando la ubicación de origen (locationId).
  // Si locationId es undefined, conserva la de la línea existente (o ninguna).
  const addToCartLine = (params, locationId) => {
    const { id, name, price, unit_type, image_path, delta, realWeight } = params;
    const idLower = (id == null ? '' : String(id)).toLowerCase();
    const loc = locationId ?? null;
    const sameLine = (item) =>
      (String(item.id) || '').toLowerCase() === idLower && (item.location_id ?? null) === loc;
    // El operario está editando el carrito del socio seleccionado ahora mismo.
    cartOwnerIdRef.current = selectedClientRef.current?.id ?? cartOwnerIdRef.current;
    const mergeRealWeight = (item) => (
      (item.real_weight != null || realWeight != null)
        ? Number(((Number(item.real_weight) || 0) + (Number(realWeight) || 0)).toFixed(3))
        : undefined
    );
    const isNullLine = (item) =>
      (String(item.id) || '').toLowerCase() === idLower && (item.location_id ?? null) === null;
    const mismoProducto = (item) => (String(item.id) || '').toLowerCase() === idLower;
    // La línea que recibe el toque, con la MISMA regla que el servidor: ubicación
    // exacta; y sin ubicación (null = «aún sin decidir»), la primera línea del
    // producto. Si aquí se abriera una segunda fila y allí se sumara a la primera,
    // el repintado la fundiría «solo» delante del operario.
    const lineaDelToque = (prevCart) =>
      prevCart.find(sameLine) || (loc === null ? prevCart.find(mismoProducto) : undefined);
    // El peso real que verá el servidor: el de la línea tal y como queda en pantalla
    // (quien tiene la báscula delante manda; el incremento lo SUSTITUYE, no lo suma).
    const lineaPrevia = lineaDelToque(cartRef.current || []) || (loc !== null ? (cartRef.current || []).find(isNullLine) : null);
    const pesoReal = mergeRealWeight(lineaPrevia || {});
    setCart((prevCart) => {
      const exists = lineaDelToque(prevCart);
      let updatedCart;
      if (exists) {
        updatedCart = prevCart.map((item) =>
          item === exists
            ? { ...item, quantity: Number((item.quantity + delta).toFixed(3)), real_weight: mergeRealWeight(item) }
            : item
        );
      } else if (loc !== null && prevCart.some(isNullLine)) {
        // Ya hay una línea de este producto SIN ubicación (null = "aún sin decidir", no una
        // ubicación real). Al añadir con la ubicación L, absorberla (sumar y fijar L) en vez
        // de crear una segunda línea. Dos neveras = dos líneas es legítimo; null no lo es.
        let absorbed = false;
        updatedCart = prevCart.map((item) => {
          if (isNullLine(item) && !absorbed) {
            absorbed = true;
            return { ...item, location_id: locationId, quantity: Number((item.quantity + delta).toFixed(3)), real_weight: mergeRealWeight(item) };
          }
          return item;
        });
      } else {
        updatedCart = [
          ...prevCart,
          { id, name, price, unit_type, image_path, quantity: Number(delta.toFixed(3)), real_weight: realWeight, location_id: locationId },
        ];
      }
      // Marcar el array que produce ESTE toque (el que verá el efecto) como «ya viajó
      // como incremento». Es la única marca que se pone dentro del updater: añadir a un
      // WeakSet es idempotente, así que si React lo reinvoca no pasa nada.
      if (!incrementosNoSoportadosRef.current) carritosDeToqueRef.current.add(updatedCart);
      return updatedCart;
    });
    // Feedback FUERA del updater de setCart: setToast/setCartAnimation/setTimeout son efectos
    // secundarios, y React reinvoca el updater (siempre en StrictMode) → timers duplicados.
    setCartAnimation(true);
    if (cartAnimTimerRef.current) clearTimeout(cartAnimTimerRef.current);
    cartAnimTimerRef.current = setTimeout(() => setCartAnimation(false), 200);
    // Contador: si es el mismo producto dentro de la ventana del toast, acumula («Añadido ×3»).
    const prevCount = toastCountRef.current;
    toastCountRef.current = (prevCount && prevCount.id === idLower)
      ? { id: idLower, n: prevCount.n + 1 }
      : { id: idLower, n: 1 };
    const n = toastCountRef.current.n;
    setToast({ text: n > 1 ? `Añadido ×${n} · ${name}` : `Añadido · ${name}` });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => { setToast(null); toastCountRef.current = null; }, 1800);
    if (incrementosNoSoportadosRef.current) {
      // Backend sin el endpoint aditivo: el camino de siempre (guardado completo rápido).
      scheduleDraftSync(80);
      return;
    }
    // El toque viaja como incremento (+delta de ESTA línea), no como el carrito entero.
    enviarIncrementoRef.current?.({ id, price, delta, location_id: loc, real_weight: pesoReal });
  };

  // Handler to add a product to the cart (case-insensitive ID comparison to prevent duplicate lines from UUID casing differences)
  const handleAddToCart = (product, event) => {
    const { id, name, unit_type, quantity: delta, image_path, realWeight } = product;
    // Precio según la línea de coste del socio seleccionado (cae al normal si no hay socio/línea)
    const price = effectiveUnitPrice(product, selectedClient?.profile?.cost_line);
    const idLower = (id == null ? '' : String(id)).toLowerCase();
    // Anti clic-fantasma: un doble-tap (o doble-disparo del navegador) emite 2 onClick sobre
    // el MISMO botón en < 50 ms y en el píxel exacto → +2 sin querer. La guarda ignora SOLO
    // ese rebote; los clics rápidos deliberados del operario siguen sumando. Sin `event`
    // (llamadas internas: elección de ubicación, merge del pendiente) no se aplica guarda.
    if (addGuardRef.current.shouldIgnore(idLower, event)) return;
    // Club social / Productos: si no hay socio, acumular en carrito pendiente y abrir carrito
    if (embedProductosInControl && isProductosMode && !selectedClient?.id) {
      const mergeRealWeight = (item) => (
        (item.real_weight != null || realWeight != null)
          ? Number(((Number(item.real_weight) || 0) + (Number(realWeight) || 0)).toFixed(3))
          : undefined
      );
      if (!showCart && (!pendingCartRaw || pendingCartRaw.length === 0)) {
        setShowCart(true);
      }
      setPendingCartRaw((prevCart) => {
        const exists = prevCart.find((item) => (String(item.id) || '').toLowerCase() === idLower);
        if (exists) {
          return prevCart.map((item) =>
            (String(item.id) || '').toLowerCase() === idLower
              ? { ...item, quantity: Number((Number(item.quantity) + delta).toFixed(3)), real_weight: mergeRealWeight(item) }
              : item
          );
        }
        return [
          ...prevCart,
          { id, name, price, unit_type, image_path, quantity: Number(delta.toFixed(3)), real_weight: realWeight },
        ];
      });
      return;
    }

    // Regla de oro (Fase D): si el producto está en 2+ ubicaciones de sala con stock,
    // PREGUNTAR de cuál sale — SIEMPRE, en cada add (no solo la primera vez). Si está en
    // 1 sola, se fija silenciosa; si en 0, la decide el backend (principal).
    const params = { id, name, price, unit_type, image_path, delta, realWeight };
    const opts = saleLocsByProduct[idLower] || [];
    if (opts.length >= 2) {
      setLocationChoice({
        detail: {
          product_name: name,
          reason: 'ambiguous',
          needed: String(delta),
          options: opts.map((o) => ({
            location_id: String(o.location_id),
            name: o.name,
            kind: o.kind,
            quantity: String(o.quantity),
            enough: Number(o.quantity) >= Number(delta),
          })),
        },
        productId: id,
        unitType: unit_type,
        pendingAdd: params,
      });
      return;
    }
    addToCartLine(params, opts.length === 1 ? opts[0].location_id : undefined);
  };

  // When a client is selected while we have a pending cart (club social productos),
  // merge pending items into the loaded draft cart once fetch completes, then sync.
  useEffect(() => {
    if (!selectedClient?.id) return;
    if (!embedProductosInControl || !isProductosMode) return;
    if (!pendingCartCarryRef.current || !Array.isArray(pendingCartCarryRef.current) || pendingCartCarryRef.current.length === 0) return;
    if (isLoadingDraftRef.current) return;

    const carry = pendingCartCarryRef.current;
    pendingCartCarryRef.current = null;
    // El carrito pendiente se asigna a este socio: pasa a ser su dueño.
    cartOwnerIdRef.current = selectedClient.id;

    const merge = (base, extra) => {
      const byId = {};
      for (const it of [...(base || []), ...(extra || [])]) {
        // Clave por (producto + ubicación), consistente con fetch/sync/backend. Keyear solo
        // por producto fusionaba el MISMO producto de dos neveras en una línea (perdiendo una
        // ubicación) — la causa del "colapso de variedades" en el flujo club-social.
        const key = `${(it?.id == null ? '' : String(it.id)).toLowerCase()}|${it?.location_id ?? ''}`;
        const qty = Number(it?.quantity) || 0;
        if (qty <= 0) continue;
        if (!byId[key]) byId[key] = { ...it, quantity: qty };
        else byId[key].quantity = Number((Number(byId[key].quantity) + qty).toFixed(3));
      }
      return Object.values(byId);
    };

    setCart((prev) => {
      const merged = merge(prev, carry);

      // Sync immediately (no need to wait debounce)
      try {
        syncDraftCartRef.current(merged);
      } catch {
        // ignore
      }

      // Al asignar el carrito a un socio, mostrar "Disponible" = saldo - total carrito (con descuento),
      // usando el carrito FINAL (incluye lo que el socio ya tuviese).
      const baseBal = Number(selectedClient?.profile?.credit_balance ?? selectedClient?.profile?.cash_balance) || 0;
      creditBaseForCartOverrideRef.current = { userId: selectedClient.id, baseBalance: baseBal };
      setDisplayCreditOverride({ userId: selectedClient.id, balance: null });

      return merged;
    });

    setPendingCartRaw([]);
  }, [
    selectedClient?.id,
    selectedClient?.profile?.credit_balance,
    selectedClient?.profile?.cash_balance,
    memberDiscountRate,
    embedProductosInControl,
    isProductosMode,
  ]);
  

  const handleRemoveFromCart = (productId) => {
    const pid = (productId == null ? '' : String(productId)).toLowerCase();
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => (String(item.id) || '').toLowerCase() === pid);
      if (existingItem) {
        if (existingItem.quantity > 1) {
          return prevCart.map((item) =>
            (String(item.id) || '').toLowerCase() === pid ? { ...item, quantity: item.quantity - 1 } : item
          );
        }
        return prevCart.filter((item) => (String(item.id) || '').toLowerCase() !== pid);
      }
      return prevCart;
    });
  };

  // Operan por LÍNEA (producto + ubicación), porque el mismo producto puede tener
  // varias líneas (una por nevera). `line` es el item del carrito.
  const sameLineAs = (line) => {
    const pid = (line?.id == null ? '' : String(line.id)).toLowerCase();
    const loc = line?.location_id ?? null;
    return (item) => (String(item.id) || '').toLowerCase() === pid && (item.location_id ?? null) === loc;
  };

  const handleRemoveProduct = (line) => {
    const match = sameLineAs(line);
    setQtyWarning("");   // el aviso era de esta línea; sin línea, sobra
    setCart((prevCart) => prevCart.filter((item) => !match(item)));
  };

  const handleClearCart = async () => {
    setQtyWarning("");
    // Sin window.confirm: el modal "¿Vaciar el carrito?" (Figma) ya preguntó, y su
    // botón es el ÚNICO que llega aquí. Preguntaba dos veces, la segunda con el
    // cuadro feo del navegador — y si cancelabas ahí, el carrito NO se vaciaba
    // pero el toast cantaba "Carrito vaciado" igual. (Reporte del usuario 17-ago.)
    const clientId = selectedClient?.id;
    setCart([]);
    cartVersionRef.current = 0;      // sin carrito no hay versión que exigir
    baseServerCartRef.current = [];
    // Un incremento aún en vuelo respondería con líneas que el operario acaba de
    // vaciar: invalidar esas respuestas (sigueVigente) igual que hace una recarga.
    fetchDraftVersionRef.current += 1;
    if (debouncedSyncRef.current) { clearTimeout(debouncedSyncRef.current); debouncedSyncRef.current = null; }
    if (selectedClient) {
      localStorage.removeItem(`cartItems_${selectedClient.id}_${cartType}`);
    }
    setShowCart(false);
    if (clientId && venueID && token) {
      // Por la cadena, detrás de los toques encolados: un DELETE suelto adelantaría a
      // un incremento en vuelo y ese incremento dejaría un carrito fantasma. El aviso
      // al menú (#738, avisarCarritosCambiados) va en el finally de performSyncDraftCart.
      await enqueueDraftSyncRef.current?.([]);
    }
  };


  // El operario eligió una ubicación en el mini-diálogo → fijarla en la línea del
  // carrito; el cambio dispara el sync de nuevo y ahora el backend reserva de ahí.
  const handleChooseLocation = (locationId) => {
    const choice = locationChoice;
    setLocationChoice(null);
    if (!choice) return;
    setErrorMessage('');
    // Proactivo (al añadir): ahora sí metemos la línea con la ubicación elegida.
    if (choice.pendingAdd) {
      addToCartLine(choice.pendingAdd, locationId);
      return;
    }
    // Reactivo (409 del backend): fijar la ubicación en la línea ya existente.
    const pid = (choice.productId == null ? '' : String(choice.productId)).toLowerCase();
    if (!pid) return;
    setCart((prev) => prev.map((it) =>
      (String(it.id) || '').toLowerCase() === pid ? { ...it, location_id: locationId } : it
    ));
  };

  // Handler to perform checkout — opens PaymentModal instead of simple confirm
  const handleCheckout = async () => {
    if (!selectedClient) {
      setErrorMessage("Por favor, selecciona un cliente");
      return;
    }
    if (cart.length === 0) {
      setErrorMessage("El carrito está vacío");
      return;
    }
    
    // Misma cuenta que la barra y que el panel: `calcularTotalesCarrito` es la
    // única fórmula del flujo, para que lo que se enseña y lo que se cobra no
    // puedan volver a divergir.
    const {
      subtotal,
      descuento: discountAmount,
      total: totalWithDiscount,
    } = calcularTotalesCarrito(cart, memberDiscountRate);

    // #487 (punto 4): nada de guardados en vuelo mientras se cobra. Lo pendiente
    // se asienta AQUÍ — el servidor tendrá exactamente lo que el modal enseña, y
    // make_purchase (ya con su lock, #486) consumirá esa foto y no otra.
    if (debouncedSyncRef.current) {
      clearTimeout(debouncedSyncRef.current);
      debouncedSyncRef.current = null;
    }
    await flushPendingDraftSync();

    // Cerrar el panel del carrito para que el PaymentModal (z-50) no quede detrás del portal del carrito (z-index muy alto), sobre todo en móvil.
    setShowCart(false);

    setPendingPurchase({
      totalAmount: totalWithDiscount,
      subtotal,
      discountAmount,
      discountLabel: memberDiscountLabel,
      clientEmail: selectedClient.email,
      clientName: selectedClient?.profile?.first_name || selectedClient?.profile?.name || '',
    });
    setShowPurchaseConfirm(true);
  };

  // Callback after PaymentModal completes (cash, card or credit)
  const handlePaymentComplete = async (result) => {
    setShowPurchaseConfirm(false);
    setErrorMessage("");

    const clientId = selectedClient?.id;
    if (
      result?.method === 'credit' &&
      clientId != null &&
      result.newBalance != null &&
      Number.isFinite(Number(result.newBalance))
    ) {
      const nb = Number(result.newBalance);
      const mergeBalance = (client) => {
        if (!client || String(client.id) !== String(clientId)) return client;
        return {
          ...client,
          profile: {
            ...(client.profile || {}),
            credit_balance: nb,
            cash_balance: nb,
          },
        };
      };

      updateUserCredit(clientId, nb);
      // Bus de saldo (plan CRUO §E): la venta del POS también avisa al resto.
      avisarSaldoCambiado(clientId, nb);
      setClients((prev) => prev.map((client) => mergeBalance(client)));
      setSelectedClient((prev) => mergeBalance(prev));
      setModalClient((prev) => mergeBalance(prev));
      setDisplayCreditOverride({ userId: clientId, balance: nb });
      if (embedProductosInControl && typeof onEmbeddedVenueCreditSync === 'function') {
        await onEmbeddedVenueCreditSync(clientId, nb);
      } else {
        await refreshUsersInClub({ creditPatch: { userId: clientId, balance: nb } });
      }
    }

    setPurchaseSuccessData({
      totalAmount: pendingPurchase?.totalAmount ?? 0,
      clientEmail: pendingPurchase?.clientEmail,
      clientName: pendingPurchase?.clientName,
      paymentMethod: result.method,
    });
    // Reset a prueba de balas: una venta nueva SIEMPRE empieza limpia (sin arrastrar
    // la ubicación elegida en la venta anterior → el popup "¿de dónde sale?" debe
    // volver a preguntar si el producto está en 2+ ubicaciones).
    setCart([]);
    setPendingCartRaw([]);
    setLocationChoice(null);
    // Cobro hecho: la clave de ESE cobro muere aquí mismo, también la guardada (el
    // efecto de la huella la renovará al vaciarse el carrito; esto es el cinturón
    // por si la siguiente venta fuera idéntica línea a línea antes de que corra).
    olvidarClaveCobro(venueID);
    claveCobroRef.current = nuevaClaveIdempotencia();
    // La venta nueva empieza sin versión: un toque con la versión del carrito
    // recién cobrado recibiría 409 cart_gone (RF-003) y se perdería. Y un incremento
    // en vuelo no debe repintar líneas ya cobradas (sigueVigente).
    cartVersionRef.current = 0;
    baseServerCartRef.current = [];
    fetchDraftVersionRef.current += 1;
    if (clientId) {
      try {
        await fetch(`/v1/draft-cart?user_id=${clientId}&venue_id=${venueID}&cart_type=${cartType}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        console.warn('Delete draft cart failed:', e);
      } finally {
        // Cobrado = carrito fuera. Sin este aviso el número del menú se quedaba en 1
        // hasta el sondeo (un minuto) o hasta cambiar de pantalla (reporte 12-09).
        // Haya ido bien o mal el DELETE, el menú vuelve a preguntar la verdad.
        avisarCarritosCambiados();
      }
      // borrar AMBOS tipos por si el carrito se construyó en el otro
      localStorage.removeItem(`cartItems_${clientId}_dispensario`);
      localStorage.removeItem(`cartItems_${clientId}_productos`);
    }

    setSelectedClient(null);
    setDetailModal(false);
    setModalClient(null);
    localStorage.removeItem("selectedClientId");

    // Sin getTransactions(): el socio se acaba de soltar, no hay historial que pintar.
    await fetchProducts();
    fetchBoardRef.current?.();

    setShowPurchaseSuccess(true);
    setPendingPurchase(null);
  };

  // Filter clients for the inline "Retirada" dropdown
  // SOLO mostrar socios que están actualmente en sala (usersInClub)
  // Excluir el cliente que ya está seleccionado
  const coincideRetirada = filtroBusqueda(searchFieldNueva);
  const coincidePendiente = filtroBusqueda(pendingClientSearch);
  const filteredClients = usersInClub.filter((client) => {
    // Excluir el cliente seleccionado actualmente
    if (selectedClient && client.id === selectedClient.id) {
      return false;
    }
    
    if (!searchFieldNueva) return false;
    const searchTerm = searchFieldNueva.trim().toLowerCase();
    // Regla única: se busca el mismo número que se pinta (legacy si existe, interno si no)
    const numSocio = String(socioNum(client) ?? '').trim().toLowerCase();

    // Si el término de búsqueda es solo numérico, buscar principalmente por el Nº de socio
    const isNumericSearch = /^\d+$/.test(searchTerm);

    if (isNumericSearch) {
      // Búsqueda numérica: solo por el Nº de socio visible
      return numSocio === searchTerm || numSocio.includes(searchTerm);
    }
    // Búsqueda de texto: nombre, apellido, email y Nº de socio alfanumérico (p. ej. "44HWVOO")
    return coincideRetirada([
      client.profile?.first_name || client.profile?.name,
      client.profile?.last_name,
      client.email,
      numSocio,
    ]);
  });
  
  
  
  // Toggle handler for the Cart drawer
  const handleToggleCart = () => {
    setShowCart((prevShowCart) => !prevShowCart);
  };

  // La línea del carrito NO lleva el stock (se construye con campos fijos), así que
  // se mira el producto que ya está en memoria.
  //
  // El tope es el stock FÍSICO, no el `available`: ese resta lo reservado en
  // carritos abiertos —incluido el que estás editando— y solo se refresca tras el
  // guardado con retardo, así que dos ediciones seguidas lo dejaban desfasado y el
  // aviso no salía (revisión 17-ago). El físico no se mueve al teclear, así que da
  // un tope estable, y sirve igual para el carrito sin socio, que no reserva nada.
  // Avisa, no bloquea: el stock puede ir desfasado y no vamos a impedir una venta
  // legítima en plena barra.
  const checkStockForQty = (line, qty) => {
    const prod = products.find((p) => String(p.id).toLowerCase() === String(line?.id ?? '').toLowerCase());
    if (!prod) { setQtyWarning(""); return; }
    const fisico = Number(prod.stock_quantity != null ? prod.stock_quantity : prod.available);
    if (!Number.isFinite(fisico)) { setQtyWarning(""); return; }
    const unidad = prod.unit_type === 'weight' ? 'g' : 'u';
    setQtyWarningAllowsNegative(Boolean(prod.is_dispensario));
    setQtyWarning(qty > fisico
      ? `Ojo: de ${line?.name || 'este producto'} solo hay ${fisico} ${unidad} en stock.`
      : "");
  };

  // El aviso es de UNA línea concreta: al cambiar de socio o al quedarse el carrito
  // vacío (tras cobrar, p. ej.) deja de tener sentido, y no puede heredarlo el
  // siguiente cliente que se atienda (revisión 17-ago).
  const selectedClientIdForWarning = selectedClient?.id;
  const carritoVacio = cart.length === 0;
  useEffect(() => {
    setQtyWarning("");
    setQtyWarningAllowsNegative(false);
  }, [selectedClientIdForWarning, carritoVacio]);

  const handleAddQuantity = (line, delta) => {
    const match = sameLineAs(line);
    checkStockForQty(line, (Number(line?.quantity) || 0) + delta);
    setCart((prev) =>
      prev.map((i) => (match(i) ? { ...i, quantity: +(i.quantity + delta).toFixed(3) } : i))
    );
  };
  // Descuento manual de la línea (%). Se guarda en la línea del carrito y viaja al
  // checkout (make-purchase) donde se suma al descuento del socio.
  const handleChangeLineDiscount = (line, value) => {
    const match = sameLineAs(line);
    const raw = value === '' || value == null ? undefined : Math.min(100, Math.max(0, Number(value)));
    const apply = (i) => (match(i) ? { ...i, manual_discount: raw } : i);
    if (selectedClient) setCart((prev) => prev.map(apply));
    else setPendingCartRaw((prev) => prev.map(apply));
  };
  // Cantidad ABSOLUTA tecleada en el carrito (petición de Kong, 16-ago). No borra
  // líneas: un 0 o un vacío se ignora — para quitar está la X. El guardado del
  // borrador ya va con retardo, así que teclear "15" hace MENOS peticiones que
  // pulsar quince veces el +.
  const handleSetQuantity = (line, value) => {
    const match = sameLineAs(line);
    const qty = Math.round(Number(value) || 0);
    if (qty <= 0) return;
    checkStockForQty(line, qty);
    setCart((prev) => prev.map((i) => (match(i) ? { ...i, quantity: qty } : i)));
  };
  const handleReduceQuantity = (line, delta) => {
    const match = sameLineAs(line);
    checkStockForQty(line, Math.max(0, (Number(line?.quantity) || 0) - delta));
    setCart((prev) =>
      prev
        .map((i) => (match(i) ? { ...i, quantity: Math.max(0, +(i.quantity - delta).toFixed(3)) } : i))
        .filter(i => i.quantity > 0)
    );
  };

  // Crédito PROYECTADO del socio seleccionado = saldo real − total del carrito (con descuento).
  // Preview en vivo: el saldo real NO cambia hasta validar; aquí solo se muestra la resta.
  const selectedClientRealCredit = Number(
    selectedClient?.profile?.credit_balance ?? selectedClient?.profile?.cash_balance
  ) || 0;
  // Con la misma cuenta que la barra, el panel y el cobro. La fórmula que había
  // aquí descontaba a nivel de subtotal e ignoraba el `manual_discount` de línea:
  // con una línea de €10 al 50% manual y €7 de saldo, la barra decía €5.00 y este
  // chip pintaba «€-3.00» en rojo — el mismo falso «no le llega» que se arregló en
  // `CartBar`, vivo un componente más allá.
  const selectedClientCartTotal = calcularTotalesCarrito(cart, memberDiscountRate).total;
  const selectedClientProjectedCredit = Number(
    (selectedClientRealCredit - selectedClientCartTotal).toFixed(2)
  );

  const hasBottomCartBar =
    !showCart &&
    ((selectedClient && cart.length > 0) ||
      (!selectedClient && embedProductosInControl && isProductosMode && pendingCartRaw.length > 0));
  const bottomCartBarLifted = hasBottomCartBar && selectedClient && !embedProductosInControl;

  return (
    <div
      className={[
        'wa-dispensario-page',
        hasBottomCartBar && 'wa-dispensario-page--has-cart-bar',
        bottomCartBarLifted && 'wa-dispensario-page--cart-lifted',
      ].filter(Boolean).join(' ')}
    >
      {/* Banner sticky del cliente - debe estar al inicio para que funcione el sticky */}
      {selectedClient && (
        (() => {
          const isInVenue = usersInClub.some(u => u.id === selectedClient.id);
          const documentsPath = selectedClient?.profile?.documents_path || selectedClient?.profile?.image_path;
          const imageUrl = documentsPath ? getImageUrl(documentsPath) : null;

          return (
            <div
              className={`wa-dispensario-client-banner mb-3 sm:mb-4 flex flex-nowrap gap-0 w-full ${showCart ? 'relative z-0' : 'sticky top-0 z-40'}`}
            >
              <div
                className="flex-1 min-w-0 relative transition-all duration-200"
                style={showCart ? { maxWidth: 'calc(100vw - 22rem)' } : {}}
              >
              <div className="rounded-md border border-[var(--primary)] bg-[var(--card)] shadow-sm flex flex-col gap-3 px-3 py-3 lg:flex-row lg:items-center lg:px-4 lg:py-2.5">
                {/* Botón de cerrar (X) en la esquina superior derecha exterior del banner */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSelectedClientOnPage();
                  }}
                  className="absolute top-2 right-2 lg:-top-2 lg:-right-2 z-30 text-[var(--muted-foreground)] hover:text-[var(--foreground)] bg-white hover:bg-[var(--muted)] rounded-full p-1.5 transition-colors flex items-center justify-center border border-[var(--border)] shadow-md"
                  title="Cerrar banner y deseleccionar cliente"
                  style={{ width: '28px', height: '28px' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div
                  className="flex items-start gap-3 flex-1 min-w-0 pr-10 lg:pr-8 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    
                    // Recargar el cliente desde la lista actualizada antes de abrir el modal
                    if (selectedClient?.id) {
                      try {
                        const updatedClients = await fetchClients();
                        let fullClient = updatedClients.find((c) => String(c.id) === String(selectedClient.id));
                        let clientToShow = fullClient;
                        if (!fullClient) {
                          // Si no está en clients, buscar en staff y owners (tienen profile completo)
                          const [staffList, ownersList] = await Promise.all([fetchStaff(), fetchOwners()]);
                          const staffMember = staffList.find((s) => String(s?.id) === String(selectedClient.id));
                          const owner = ownersList.find((o) => String(o?.id) === String(selectedClient.id));
                          if (staffMember) {
                            clientToShow = staffMember;
                          } else if (owner) {
                            clientToShow = owner;
                          } else {
                            // Último recurso: usersInClub o selectedClient
                            const userInClub = usersInClub.find((u) => String(u?.id) === String(selectedClient.id));
                            clientToShow = userInClub || selectedClient;
                          }
                        }
                        setModalClient(clientToShow);
                        // Usar setTimeout para asegurar que el estado se actualice antes de abrir el modal
                        setTimeout(() => {
                          setDetailModal(true);
                        }, 0);
                      } catch (error) {
                        console.error("Error loading client:", error);
                        // Si falla, usar los datos que tenemos
                        setModalClient(selectedClient);
                        setTimeout(() => {
                          setDetailModal(true);
                        }, 0);
                      }
                    } else {
                      setModalClient(selectedClient);
                      setTimeout(() => {
                        setDetailModal(true);
                      }, 0);
                    }
                  }}
                  title="Haz clic para ver la ficha del cliente"
                >
                  <div className="relative flex-shrink-0">
                    {imageUrl ? (
                      <div className="w-12 h-12 lg:w-10 lg:h-10 rounded-lg overflow-hidden border border-[var(--border)]">
                        <img
                          src={imageUrl}
                          alt={selectedClient?.email}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                      </div>
                    ) : null}
                    <div 
                      className={`w-12 h-12 lg:w-10 lg:h-10 rounded-lg bg-[var(--muted)] flex items-center justify-center text-[var(--muted-foreground)] font-semibold border border-[var(--border)] flex-shrink-0 ${imageUrl ? 'hidden' : 'flex'} text-base lg:text-sm`}
                    >
                      {(() => {
                        const firstName = selectedClient?.profile?.first_name || selectedClient?.profile?.name || '';
                        const lastName = selectedClient?.profile?.last_name || '';
                        const fullName = `${firstName} ${lastName}`.trim();
                        const displayName = fullName || selectedClient?.email || 'U';
                        return displayName.charAt(0).toUpperCase();
                      })()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-[var(--foreground)] text-base lg:text-sm max-lg:break-words">
                        {(() => {
                          const firstName = selectedClient?.profile?.first_name || selectedClient?.profile?.name || '';
                          const lastName = selectedClient?.profile?.last_name || '';
                          const fullName = `${firstName} ${lastName}`.trim();
                          return fullName || selectedClient?.email || 'Sin nombre';
                        })()}
                      </div>
                      {/* Tres estados: mientras la sala no ha contestado no se
                          pinta «Fuera» — era el rojo falso de Kong (20-09-2026).
                          Candado: SalaComprobandoNoEsFuera.wave7. */}
                      {!salaCargada ? (
                        <span className="px-2 py-0.5 bg-[var(--secondary)] text-[var(--muted-foreground)] text-[10px] sm:text-[10px] font-semibold rounded-full whitespace-nowrap">
                          Comprobando…
                        </span>
                      ) : isInVenue ? (
                        <span className="px-2 py-0.5 bg-[var(--success)] text-[var(--primary-foreground)] text-[10px] sm:text-[10px] font-semibold rounded-full whitespace-nowrap">
                          En sala
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-[var(--destructive)] text-[var(--destructive-foreground)] text-[10px] sm:text-[10px] font-semibold rounded-full whitespace-nowrap">
                          Fuera de sala
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleCart();
                        }}
                        className="bg-[var(--primary)] hover:bg-[var(--primary-strong)] text-[var(--primary-foreground)] font-bold py-1.5 px-2.5 sm:py-1 sm:px-2 rounded text-xs flex items-center gap-1 relative"
                        title={showCart ? "Ocultar carrito" : "Ver carrito"}
                      >
                        <BsCart4 className={`text-sm transition-transform duration-75 ${cartAnimation ? 'scale-125 rotate-12' : ''}`} />
                        {cart.length > 0 && (
                          <span className={`absolute -top-1 -right-1 bg-[var(--primary-strong)] rounded-full px-1.5 py-0.5 text-[10px] font-bold transition-all duration-75 ${cartAnimation ? 'scale-150 bg-green-500' : ''}`}>
                            {cart.length}
                          </span>
                        )}
                      </button>
                    </div>
                    <div className="flex flex-col gap-2 mt-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-2 lg:mt-0.5">
                      <span className="inline-flex w-fit items-center rounded-full bg-[var(--muted)] px-2.5 py-1 text-xs font-medium text-[var(--foreground)]">
                        Nº Socio: {socioNum(selectedClient) || 'N/A'}
                      </span>
                      <span className="inline-flex w-fit items-center rounded-full bg-[var(--muted)] px-2.5 py-1 text-xs text-[var(--foreground)]">
                        Crédito:{' '}
                        {/* CUARTO saldo del fichero, y llevaba la regla CONTRARIA a
                            los otros tres: `< 0` frente a `<= 0`. En la misma pantalla,
                            un socio a cero salía rojo en la lista de sala y negro en su
                            propia chip. Al helper, como los demás. */}
                        <span className={`ml-1 font-semibold ${balanceToneClass(selectedClientProjectedCredit, 'text-[var(--foreground)]')}`}>
                          {formatBalance(selectedClientProjectedCredit)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                {/* Acciones derecha: Cambiar socio (Figma) + buscador */}
                <div className="w-full lg:w-auto lg:flex-shrink-0 lg:ml-auto flex flex-col gap-2 lg:flex-row lg:items-center">
                  {/* Cambiar socio — NUEVO (Figma): deselecciona para elegir otro socio */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSelectedClientOnPage({ clearSearch: true });
                    }}
                    className="w-full lg:w-auto inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-[var(--border)] bg-white px-3 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)] transition-colors whitespace-nowrap"
                    title="Cambiar socio"
                  >
                    <HiOutlineRefresh className="text-base" />
                    Cambiar socio
                  </button>
                  <div className="relative w-full lg:w-64">
                    <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] text-base z-10 pointer-events-none" />
                    <input
                      type="text"
                      value={searchFieldNueva}
                      onChange={(e) => setSearchFieldNueva(e.target.value)}
                      placeholder={selectedClient ? `Buscar otro cliente...` : "Buscar clientes..."}
                      className="w-full h-9 rounded-md border border-[var(--border)] bg-white pl-9 pr-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {/* Dropdown de resultados para cambiar de socio sin deseleccionar */}
                    {searchFieldNueva && (
                      <div className="absolute right-0 top-full z-50 mt-1 w-full lg:w-80 max-h-80 overflow-y-auto rounded-lg border border-[var(--border)] bg-white shadow-lg">
                        {filteredClients.length === 0 ? (
                          <div className="flex flex-col items-center justify-center gap-1.5 px-4 py-6 text-center">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--muted)]">
                              <svg className="w-4 h-4 text-[var(--muted-foreground)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10 17a7 7 0 100-14 7 7 0 000 14zM13 8l-6 6M7 8l6 6" />
                              </svg>
                            </span>
                            <p className="text-sm font-medium text-[var(--foreground)]">Sin resultados</p>
                            <p className="text-xs text-[var(--muted-foreground)]">No hay socios que coincidan con tu búsqueda</p>
                          </div>
                        ) : (
                          filteredClients.map((client) => {
                            const fullName = `${client?.profile?.first_name || client?.profile?.name || ''} ${client?.profile?.last_name || ''}`.trim();
                            const bal = Number(client?.profile?.credit_balance ?? client?.profile?.cash_balance) || 0;
                            return (
                              <button
                                key={client.id}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleClientSelection(client); }}
                                className="flex w-full items-center gap-3 border-b border-[var(--border)] px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-[var(--muted)]"
                              >
                                <MemberPhoto
                                  path={client?.profile?.documents_path || client?.profile?.image_path}
                                  name={fullName || client?.email || ''}
                                  className="h-[46px] w-[46px] flex-shrink-0 rounded-lg object-cover"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 text-sm font-medium text-[var(--foreground)]">
                                    <span className="truncate">{fullName || client?.email || 'Sin nombre'}</span>
                                    <CaducadoChip socio={client} />
                                  </div>
                                  <div className="truncate text-xs text-[var(--muted-foreground)]">
                                    Nº Socio: {socioNum(client) || '—'}
                                    {' · '}
                                    Crédito: <span className={balanceToneClass(bal, 'text-[var(--foreground)]')}>{formatBalance(bal)}</span>
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()
      )}

      {/* Cabecera "¿A quién atendemos?": SOLO mientras no hay socio.
          Reporte de Kong (19-ago, producción): "una vez ya seleccionado el socio te
          vuelve a salir el recuadro de a quien atenderemos cuando es el menú de
          selección de los productos ya". Lo de dentro —el buscador y la lista de
          socios en sala— ya estaba condicionado, pero el título y el "busca un socio
          para iniciar una retirada" se pintaban siempre: quedaba un cartel pidiendo
          elegir socio justo encima del surtido, con el socio ya elegido. Para
          cambiarlo está «Cambiar socio», arriba, que no depende de esto. */
      }
      {!selectedClient && (
      <div className="mb-3 flex min-w-0 flex-col gap-4 sm:mb-4">
        {/* Cabecera y lista van en DOS tarjetas, no en una con el título pegado
            a la lista: la cabecera manda sobre la pantalla y la lista es su
            contenido. Mismo criterio que Entradas. */}
        <SectionHeader
          title="¿A quién atendemos?"
          subtitle={
            isProductosMode
              ? 'Busca un socio en sala para añadir productos.'
              : 'Busca un socio en sala para iniciar una retirada.'
          }
          count={usersInClub.length > 0 ? `${usersInClub.length} en sala` : null}
          sticky={false}
          actionsClassName="items-center"
        >
          {/* Buscador de socios cuando NO hay cliente seleccionado */}
          {!selectedClient && (
            <div className="relative w-full min-w-0 sm:w-72 sm:flex-shrink-0">
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] text-base z-10 pointer-events-none" />
              <input
                type="text"
                value={searchFieldNueva}
                onChange={(e) => setSearchFieldNueva(e.target.value)}
                placeholder="Buscar socio"
                className="w-full h-9 rounded-md border border-[var(--border)] bg-white pl-9 pr-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
                autoFocus={!selectedClient}
              />
              {/* Dropdown flotante de resultados (Figma) */}
              {searchFieldNueva && (
                <div className="absolute right-0 top-full z-50 mt-1 w-full sm:w-80 max-h-80 overflow-y-auto rounded-lg border border-[var(--border)] bg-white shadow-lg">
                  {filteredClients.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-1.5 px-4 py-6 text-center">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--muted)]">
                        <svg className="w-4 h-4 text-[var(--muted-foreground)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10 17a7 7 0 100-14 7 7 0 000 14zM13 8l-6 6M7 8l6 6" />
                        </svg>
                      </span>
                      <p className="text-sm font-medium text-[var(--foreground)]">Sin resultados</p>
                      <p className="text-xs text-[var(--muted-foreground)]">No hay socios que coincidan con tu búsqueda</p>
                    </div>
                  ) : (
                    filteredClients.map((client) => {
                      const fullName = `${client?.profile?.first_name || client?.profile?.name || ''} ${client?.profile?.last_name || ''}`.trim();
                      const bal = Number(client?.profile?.credit_balance ?? client?.profile?.cash_balance) || 0;
                      return (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleClientSelection(client)}
                          className="flex w-full items-center gap-3 border-b border-[var(--border)] px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-[var(--muted)]"
                        >
                          <MemberPhoto
                            path={client?.profile?.documents_path || client?.profile?.image_path}
                            name={fullName || client?.email || ''}
                            className="h-[46px] w-[46px] flex-shrink-0 rounded-lg object-cover"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 text-sm font-medium text-[var(--foreground)]">
                              <span className="truncate">{fullName || client?.email || 'Sin nombre'}</span>
                              <CaducadoChip socio={client} />
                            </div>
                            <div className="truncate text-xs text-[var(--muted-foreground)]">
                              Nº Socio: {socioNum(client) || '—'}
                              {' · '}
                              Crédito: <span className={balanceToneClass(bal, 'text-[var(--foreground)]')}>{formatBalance(bal)}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}
        </SectionHeader>
        {/* Sin socio y sin búsqueda: lista por defecto de SOCIOS EN SALA (igual que Entradas),
            para elegir directamente sin tener que buscar. Si no hay nadie en sala, estado vacío. */}
        {!selectedClient && !searchFieldNueva && (
          usersInClub.length > 0 ? (
            <div className="rounded-md border border-[var(--border)] bg-[var(--card)] p-4 sm:p-6">
              {/* Sigue siendo una LISTA y no una tabla, a propósito: esto no se lee
                  en columnas, se toca para empezar a atender a alguien. La foto es
                  lo que se busca de un vistazo y una celda de tabla la encoge.
                  El recuento, eso sí, es la píldora del sistema: iba en versalitas
                  grises de 12 con su propia píldora al lado. */}
              <div className="mb-3 flex items-center gap-3">
                <h2 className="text-base font-semibold text-[var(--foreground)]">Socios en sala</h2>
                <SectionCounter>{usersInClub.length}</SectionCounter>
              </div>
              <div className="rounded-lg border border-[var(--border)] divide-y divide-[var(--border)]">
                {usersInClub.map((client) => {
                  const fullName = `${client?.profile?.first_name || client?.profile?.name || ''} ${client?.profile?.last_name || ''}`.trim();
                  const bal = Number(client?.profile?.credit_balance ?? client?.profile?.cash_balance) || 0;
                  return (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => handleClientSelection(client)}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-[var(--muted)]"
                    >
                      <MemberPhoto
                        path={client?.profile?.documents_path || client?.profile?.image_path}
                        name={fullName || client?.email || ''}
                        className="h-[46px] w-[46px] flex-shrink-0 rounded-lg object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-sm font-medium text-[var(--foreground)]">
                          <span className="truncate">{fullName || client?.email || 'Sin nombre'}</span>
                          <CaducadoChip socio={client} />
                        </div>
                        <div className="truncate text-xs text-[var(--muted-foreground)]">
                          Nº Socio: {socioNum(client) || '—'}
                          {' · '}
                          Crédito: <span className={balanceToneClass(bal, 'text-[var(--foreground)]')}>{formatBalance(bal)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--card)] py-12 text-center">
              <BsCart4 className="text-[var(--muted-foreground)]" size={64} aria-hidden />
              <p className="text-base font-semibold text-[var(--foreground)]">No hay socios en sala</p>
              <p className="text-sm text-[var(--muted-foreground)]">Busca un socio arriba para empezar</p>
            </div>
          )
        )}
      </div>
      )}





      {/* Surtido productos/dispensario — solo visible con socio (requisito legal).
          En Entradas embebido (Productos) se permite carrito pendiente sin socio. */}
      {(selectedClient || (embedProductosInControl && isProductosMode)) && (
        <div className={`grid grid-cols-2 ${compactUntilLg ? "lg:grid-cols-4" : "md:grid-cols-4"} gap-3 sm:gap-4 mt-3 sm:mt-4`}>
          <div
            className={[
              'wa-dispensario-shell p-3 sm:p-4',
              'col-span-2',
              compactUntilLg ? 'lg:col-span-4' : 'md:col-span-4',
              'min-w-0',
            ].join(' ')}
          >
            <ProductGridCart
              key={cartType}
              products={products}
              loading={!loadedProducts}
              onAddToCart={handleAddToCart}
              costLine={selectedClient?.profile?.cost_line}
              discountRate={memberDiscountRate}
              discountLabel={memberDiscountLabel}
              stockByLoc={stockByLocProduct}
            />
          </div>
        </div>
      )}

      {showCart && typeof document !== 'undefined' && document.body && ReactDOM.createPortal(
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483647,
            pointerEvents: 'none',
          }}
        >
          {/* Overlay que tapa toda la página (incl. banner); clic cierra el panel */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1,
              pointerEvents: 'auto',
              backgroundColor: 'rgba(0,0,0,0.2)',
            }}
            onClick={handleToggleCart}
            aria-hidden="true"
          />
          {/* Geometría del panel: anclado top-16 + bottom-0, SIN h-full ni 100vh.
              En Chrome de Android `100vh` es el viewport GRANDE (el de la barra de URL
              oculta) y además ignora la barra de navegación del sistema: el panel medía
              más que la pantalla visible y, como él mismo es el contenedor del scroll,
              su último tramo quedaba bajo el corte SIN forma de alcanzarlo — en la tablet
              apaisada del club desaparecían "Seguir añadiendo" y "Vaciar carrito" y
              "Validar compra" salía cortado (reporte Kong House, 08-2026). Anclar a
              bottom:0 lo ata al borde visible real, sin depender de `dvh` (que el Chrome
              viejo de la Galaxy A8 puede no soportar). Es la misma geometría que ya usa
              el dispensario nuevo (.df-cart). NO reintroducir h-full/100vh aquí.

              Y el panel NO scrollea él mismo: es una columna (cabecera · lista · pie) y
              solo scrollea la lista, dentro de `Cart`. Cuando el panel entero era el
              scroll, el pie iba detrás de las líneas y su `sticky bottom-0` solo se pegaba
              mientras su bloque contenedor estuviera a la vista: con 12 líneas quedaba
              en y=1063 de 800 y el camarero abría el carrito lleno sin ver «Validar
              compra» (Kong House, 08-2026; medido 29-08). Mismo patrón que CarritoPanel.
              Candados: tests/e2e/cart-tablet-landscape.spec.js y
              CartTabletReachability.wave5. */}
          <div
            data-testid="dispenser-cart-panel"
            className={`fixed top-16 bottom-0 right-0 flex w-full max-w-[100vw] flex-col overflow-hidden sm:max-w-md md:max-w-[33rem] ${compactUntilLg ? "lg:w-[33rem]" : "md:w-[33rem]"} bg-white shadow-2xl border-l border-gray-200`}
            style={{ pointerEvents: 'auto', zIndex: 2 }}
          >
            {/* Club_CartHeader (Figma 3196:25166). La variante «Todo» sale sola
                cuando el socio tiene líneas también en la otra área. */}
            <CartHeader
              tipo={otherAreaItems.length > 0 ? 'todo' : 'dispensario'}
              member={
                selectedClient
                  ? {
                      name: [
                        selectedClient?.profile?.first_name || selectedClient?.profile?.name,
                        selectedClient?.profile?.last_name,
                      ]
                        .filter(Boolean)
                        .join(' '),
                      member_number: socioNum(selectedClient) || 'N/A',
                      status: selectedClient?.profile?.status,
                      documents_path: selectedClient?.profile?.documents_path,
                    }
                  : null
              }
              balance={
                selectedClient
                  ? Number(selectedClient?.profile?.credit_balance ?? selectedClient?.profile?.cash_balance) || 0
                  : null
              }
              onClose={handleToggleCart}
            />

            {/* Club social / Productos: si no hay socio seleccionado, elegir socio aquí */}
            {!selectedClient && embedProductosInControl && isProductosMode && pendingCartRaw.length > 0 && (
              <div className="mx-3 mt-3 max-h-[45%] shrink-0 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50 p-3 sm:mx-4 sm:mt-4">
                <div className="text-sm font-semibold text-amber-900">Selecciona un socio para asignar este carrito</div>
                <div className="mt-2">
                  <input
                    type="text"
                    value={pendingClientSearch}
                    onChange={(e) => setPendingClientSearch(e.target.value)}
                    placeholder="Buscar socio por nombre, email o Nº socio..."
                    className="w-full px-3 py-2 text-sm bg-white border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300"
                  />
                </div>
                <div className="mt-2 max-h-48 overflow-auto rounded-lg border border-amber-200 bg-white">
                  {(sociosEnSalaForPendingCart || [])
                    .filter((u) =>
                      coincidePendiente([
                        u.profile?.first_name || u.profile?.name,
                        u.profile?.last_name,
                        u.email,
                        socioNum(u),
                      ])
                    )
                    .slice(0, 30)
                    .map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          pendingCartCarryRef.current = pendingCartRaw;
                          setPendingCartRaw([]);
                          setPendingClientSearch("");
                          handleClientSelection(u);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 border-b last:border-b-0 border-amber-100"
                      >
                        <span className="font-semibold text-gray-900">
                          {(u.profile?.first_name || u.profile?.name || u.email || "Socio")}{u.profile?.last_name ? ` ${u.profile.last_name}` : ""}
                        </span>
                        <span className="text-gray-500"> · Nº {socioNum(u) ?? "—"}</span>
                      </button>
                    ))}
                </div>
                <div className="mt-2 text-xs text-amber-800">
                  El carrito se guardará en el socio que elijas.
                </div>
              </div>
            )}

            <Cart
              cartItems={selectedClient ? cart : pendingCartRaw}
              locById={locById}
              clientCredit={
                // Con el respaldo `?? cash_balance`, como las otras ocho lecturas
                // de saldo de este fichero: era la única sin él, así que a un
                // socio cuyo perfil solo trae `cash_balance` el panel le decía
                // «Saldo €0.00» y «queda debiendo» mientras la barra —que sí lo
                // lleva— decía que iba sobrado. Y `null`, no `0`, cuando no hay
                // socio: `Cart` ya distingue los dos casos y con `0` el pie se
                // inventaba el saldo de un socio que todavía no existe.
                selectedClient
                  ? (Number(
                      selectedClient?.profile?.credit_balance
                        ?? selectedClient?.profile?.cash_balance
                    ) || 0)
                  : null
              }
              discountRate={memberDiscountRate}
              onRemoveFromCart={selectedClient ? handleRemoveFromCart : undefined}
              onRemoveProduct={selectedClient ? handleRemoveProduct : (line) => setPendingCartRaw((prev) => prev.filter((i) => String(i.id) !== String(line?.id)))}
              onClearCart={() => setShowClearConfirm(true)}
              onAddQuantity={selectedClient ? handleAddQuantity : (line, delta) => setPendingCartRaw((prev) => prev.map((i) => (String(i.id) === String(line?.id) ? { ...i, quantity: +(Number(i.quantity) + delta).toFixed(3) } : i)))}
              onSetQuantity={selectedClient ? handleSetQuantity : (line, value) => {
                const qty = Math.round(Number(value) || 0);
                if (qty <= 0) return;
                checkStockForQty(line, qty);
                setPendingCartRaw((prev) => prev.map((i) => (String(i.id) === String(line?.id) ? { ...i, quantity: qty } : i)));
              }}
              onReduceQuantity={selectedClient ? handleReduceQuantity : (line, delta) => setPendingCartRaw((prev) => prev.map((i) => (String(i.id) === String(line?.id) ? { ...i, quantity: Math.max(0, +(Number(i.quantity) - delta).toFixed(3)) } : i)).filter((i) => (Number(i.quantity) || 0) > 0))}
              onCheckout={() => {
                if (!selectedClient && embedProductosInControl && isProductosMode) {
                  setErrorMessage("Selecciona un socio para asignar el carrito antes de validar.");
                  return;
                }
                handleCheckout();
              }}
              onContinueShopping={handleToggleCart}
              onAddCredit={selectedClient ? () => { setCreditUserId(selectedClient.id); setCreditModal(true); } : undefined}
              error={errorMessage}
              stockWarning={qtyWarning}
              stockWarningAllowsNegative={qtyWarningAllowsNegative}
              otherArea={selectedClient ? { cartType: otherCartType, items: otherAreaItems } : null}
              onChangeLineDiscount={handleChangeLineDiscount}
            />
          </div>
        </div>,
        document.body
      )}
      
      {/* Barra inferior: total + Ver carrito (Figma) */}
      {hasBottomCartBar && (() => {
        const barItems = selectedClient ? cart : pendingCartRaw;
        // Con descuentos, como el panel y como el cobro. Sumar precio × cantidad
        // aquí hacía que la barra dijera «Total: €10.00» mientras se cobraban
        // €5.00, y que su aviso de saldo —que compara contra este número— diera
        // por insuficiente un saldo que llegaba de sobra.
        // Sin socio seleccionado `memberDiscountRate` es 0, así que el carrito
        // pendiente solo lleva los descuentos manuales de línea.
        const barTotal = calcularTotalesCarrito(barItems, memberDiscountRate).total;
        const barCount = barItems.length;
        return (
          <div
            className={`fixed left-0 md:left-64 right-0 z-40 transition-[bottom] duration-200 ${
              selectedClient && !embedProductosInControl
                ? (historyOpen ? "bottom-[32vh]" : "bottom-14")
                : "bottom-0"
            }`}
          >
            <CartBar
              total={barTotal}
              count={barCount}
              credit={selectedClient ? (Number(selectedClient?.profile?.credit_balance ?? selectedClient?.profile?.cash_balance) || 0) : null}
              onViewCart={handleToggleCart}
              animating={cartAnimation}
            />
          </div>
        );
      })()}

      {/* Toast transitorio "Producto añadido" / "Carrito vaciado" (Figma).
          En un portal con el MISMO z máximo que el panel del carrito y montado
          después: el panel (portal a z máximo, abajo a la derecha) tapaba los
          avisos, incluido «Ese carrito ya se cobró…» (visto en staging el 12-09).
          Fuera de la escalera de capas por la misma razón que el panel. */}
      {toast && typeof document !== 'undefined' && document.body && ReactDOM.createPortal(
        <div
          role="status"
          style={{ position: 'fixed', bottom: '5rem', right: '1rem', zIndex: 2147483647 }}
          className="flex items-center gap-2 rounded-lg border border-[var(--border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--foreground)] shadow-lg"
        >
          <svg className="w-4 h-4 text-[var(--primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {toast.text}
        </div>,
        document.body
      )}

      {/* Confirmación "¿Vaciar el carrito?" (Figma) */}
      {showClearConfirm && typeof document !== 'undefined' && document.body && ReactDOM.createPortal(
        <div
          role="dialog"
          aria-modal="true"
          style={{ position: 'fixed', inset: 0, zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div
            style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)' }}
            onClick={() => setShowClearConfirm(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-sm rounded-2xl border border-[var(--border)] bg-white p-6 shadow-2xl text-center">
            <h3 className="text-base font-semibold text-[var(--foreground)]">¿Vaciar el carrito?</h3>
            <p className="mt-1.5 text-sm text-[var(--muted-foreground)]">
              Se eliminarán todos los productos añadidos. Esta acción no se puede deshacer.
            </p>
            <div className="mt-5 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (selectedClient) {
                    handleClearCart();
                  } else {
                    setPendingCartRaw([]);
                  }
                  setShowClearConfirm(false);
                  setToast({ text: 'Carrito vaciado' });
                  setTimeout(() => setToast(null), 1800);
                }}
                className="rounded-md px-4 py-2 text-sm font-semibold text-[var(--destructive)] hover:bg-[#FEF2F2] transition-colors"
              >
                Vaciar carrito
              </button>
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                className="rounded-md border border-[var(--border)] bg-white px-4 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)] transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {embedProductosInControl ? (
        <ClientTransactions transactions={transactions} clientId={selectedClient?.id} onChanged={historialCambiado} />
      ) : (
        selectedClient && (
          <>
            {/* Espaciador para que el surtido no quede oculto tras el cajón fijo de historial */}
            <div
              aria-hidden
              className={
                historyOpen
                  ? 'h-[32vh]'
                  : 'wa-dispensario-history-spacer--with-cart'
              }
            />
            {/* Cajón inferior fijo: historial del socio siempre visible mientras se dispensa.
                Alineado con la columna de contenido (sidebar = md:w-64). */}
            <div
              className={`fixed bottom-0 left-0 md:left-64 right-0 z-30 bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.10)] flex flex-col transition-[height] duration-200 ${
                historyOpen ? "h-[32vh]" : "h-14"
              }`}
            >
              <button
                type="button"
                onClick={() => setHistoryOpen((o) => !o)}
                className="flex items-center justify-between gap-2 px-4 h-14 flex-shrink-0 border-b border-gray-100 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2 font-semibold text-gray-800 text-sm">
                  <HiOutlineClock className="text-base text-purple-600" />
                  Historial del socio
                  <span className="text-gray-400 font-normal truncate max-w-[40vw]">
                    {selectedClient?.profile?.first_name} {selectedClient?.profile?.last_name}
                  </span>
                </span>
                <HiOutlineChevronDown
                  className={`text-lg text-gray-500 transition-transform ${historyOpen ? "" : "rotate-180"}`}
                />
              </button>
              {historyOpen && (
                <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-2">
                  <ClientTransactions transactions={transactions} clientId={selectedClient?.id} bare onChanged={historialCambiado} />
                </div>
              )}
            </div>
          </>
        )
      )}

      {/* Modal de detalles del cliente */}
      {detailModal && modalClient && (
        <ClientDetailModal
          key={`${modalClient.id}-${detailModal}`}
          active={detailModal}
          handleModal={() => {
            justEditedClientIdRef.current = null; // Permitir de nuevo que los efectos actúen
            setDetailModal(false);
            setTimeout(() => setModalClient(null), 100);
          }}
          client={modalClient}
          onNotesSaved={(clientId, cambios) => {
            const patch = (prev) =>
              prev && String(prev.id) === String(clientId)
                ? { ...prev, profile: { ...(prev.profile || {}), ...cambios } }
                : prev;
            setSelectedClient(patch);
            setModalClient(patch);
            setClients((prev) => prev.map(patch));
            updateUserInClub?.(clientId, { profile: { ...cambios } });
          }}
          onBalanceChanged={aplicarSaldoAnulado}
          token={token}
          venueID={venueID}
          onEdit={handleModalEdit}
          onDelete={handleModalDelete}
          onAddCredit={handleModalAddCredit}
          dispenserContext
        />
      )}

      <NotaSocioPopup socio={avisoSocio} onClose={() => setAvisoSocio(null)} />
      {/* Modal de agregar crédito (solo en Retirada) */}
      <AddCashModal
        active={creditModal}
        handleAddCashModal={() => {
          setCreditModal(false);
          setCreditUserId(null);
          // Cerrar también el detailModal si está abierto
          if (detailModal) {
            setDetailModal(false);
            setTimeout(() => setModalClient(null), 100);
          }
        }}
        venueID={venueID}
        token={token}
        user_id={creditUserId || modalClient?.id}
        currentBalance={
          (String(selectedClient?.id) === String(creditUserId || modalClient?.id)
            ? (selectedClient?.profile?.credit_balance ?? selectedClient?.profile?.cash_balance)
            : (modalClient?.profile?.credit_balance ?? modalClient?.profile?.cash_balance)) ?? 0
        }
        setErrorMessage={setErrorMessage}
        onCashAdded={async (updatedUserId, newBalance) => {
          const bal = Number(newBalance);
          const balNum = Number.isFinite(bal) ? bal : 0;
          justAddedCreditIdRef.current = updatedUserId;
          setDisplayCreditOverride({ userId: updatedUserId, balance: balNum });
          const mergeBalance = (user) => {
            if (!user || String(user.id) !== String(updatedUserId)) return user;
            return {
              ...user,
              profile: {
                ...(user.profile || {}),
                credit_balance: balNum,
                cash_balance: balNum
              }
            };
          };
          // Nunca seleccionar al socio en productos solo por haber cargado crédito: solo actualizar saldo si ya era el seleccionado / abierto en detalle
          if (String(selectedClient?.id) === String(updatedUserId)) {
            setSelectedClient(mergeBalance(selectedClient));
          }
          if (String(modalClient?.id) === String(updatedUserId)) {
            setModalClient(mergeBalance(modalClient));
          }
          updateUserCredit(updatedUserId, balNum);
          setClients((prev) => prev.map((c) => mergeBalance(c)));
          const creditPatch = { userId: updatedUserId, balance: balNum };
          if (embedProductosInControl && typeof onEmbeddedVenueCreditSync === 'function') {
            await onEmbeddedVenueCreditSync(updatedUserId, balNum);
          } else {
            await refreshUsersInClub({ creditPatch });
          }
          fetchClients();
          setCreditModal(false);
          setCreditUserId(null);
          setNotification({
            message: `Crédito agregado correctamente. Nuevo saldo: €${balNum.toFixed(2)}`,
            type: 'success'
          });
          setTimeout(() => {
            setNotification(null);
          }, 3000);
        }}
      />

      {/* Modal de editar cliente (solo en Retirada) */}
      <CustomerModal
        active={editModal}
        handleModal={() => {
          const wasOpen = editModal;
          const editedClientId = editUserId; // Guardar el id del socio que se editó (evita usar closure con otro socio)
          setEditModal(v => !v);
          setEditUserId(null);
          // No cerrar el detailModal ni deseleccionar: mantener al socio seleccionado
          if (wasOpen && editedClientId) {
            justEditedClientIdRef.current = editedClientId; // Los efectos de recarga no sobrescribirán (evita alternancia)
            fetchClients().then((updatedClients) => {
              setClients(updatedClients);
              refreshUsersInClub(); // Actualizar lista del buscador
              const updatedClient = updatedClients.find((c) => String(c.id) === String(editedClientId));
              if (updatedClient) {
                setSelectedClient(updatedClient);
                setModalClient(updatedClient);
              }
            });
            // Limpiar ref solo cuando el usuario cierre el modal de detalle o seleccione otro socio
          }
        }}
        onClientUpdated={(updatedClient) => {
          if (updatedClient?.id) {
            setClients((prev) => prev.map((c) => (c.id === updatedClient.id ? updatedClient : c)));
            refreshUsersInClub(); // Actualizar lista del buscador
            if (String(selectedClient?.id) === String(updatedClient.id) || String(modalClient?.id) === String(updatedClient.id)) {
              setSelectedClient(updatedClient);
              setModalClient(updatedClient);
            }
          }
        }}
        token={token}
        id={editUserId}
        clientData={editUserId ? (String(modalClient?.id) === String(editUserId) ? modalClient : String(selectedClient?.id) === String(editUserId) ? selectedClient : usersInClub.find(c => String(c.id) === String(editUserId)) ?? clients.find(c => String(c.id) === String(editUserId))) : null}
        shopId={venueID}
        setErrorMessage={setErrorMessage}
      />

      {errorMessage && <p className="text-red-500">{errorMessage}</p>}

      {/* Payment Modal (cash / card / credit) */}
      <PaymentModal
        active={showPurchaseConfirm && !!pendingPurchase}
        onClose={() => {
          setShowPurchaseConfirm(false);
          setPendingPurchase(null);
        }}
        onPaymentComplete={handlePaymentComplete}
        items={cart}
        total={pendingPurchase?.totalAmount ?? 0}
        subtotal={pendingPurchase?.subtotal}
        discountAmount={pendingPurchase?.discountAmount}
        discountLabel={pendingPurchase?.discountLabel}
        clientName={pendingPurchase?.clientName}
        clientEmail={pendingPurchase?.clientEmail}
        userId={selectedClient?.id}
        venueId={venueID}
        cartType={cartType}
        token={token}
        claveIdempotencia={claveCobroRef.current}
      />

      {/* "¿De dónde sale?" — solo si hay ambigüedad (2+ neveras) o falta stock (Fase D) */}
      <LocationChoiceModal
        active={!!locationChoice}
        detail={locationChoice?.detail}
        unitType={locationChoice?.unitType}
        onClose={() => setLocationChoice(null)}
        onChoose={handleChooseLocation}
      />

      {/* Aviso de compra completada: toast que se desvanece solo, sin OK que pulsar
          (petición Kong Club, 14-ago — la validación queda en un solo paso: el
          modal de confirmación). No bloquea la pantalla. */}
      {showPurchaseSuccess && purchaseSuccessData && (
        <div className="fixed left-1/2 top-[12%] z-[100] w-full max-w-sm -translate-x-1/2 rounded-lg border border-green-200 bg-green-50 p-4 shadow-2xl">
          <div className="flex items-start">
            <svg className="w-8 h-8 text-green-600 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-lg font-semibold text-green-800 mb-1">
                ✓ Compra completada
              </p>
              <p className="text-sm text-green-700">
                Total: <span className="font-semibold">
                  {purchaseSuccessData.totalAmount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span> €
                {purchaseSuccessData.clientName ? (
                  <span className="text-green-600"> · {purchaseSuccessData.clientName}</span>
                ) : null}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notificación de producto añadido - removida, ahora se muestran chispitas en el botón */}
      {/* {notification && (
        <div className="fixed top-20 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-pulse transform transition-all duration-300 ease-in-out">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-semibold">{notification.message}</span>
        </div>
      )} */}
    </div>
  );
}

export default Dispenser;
